// THE BOX, ITS ENVIRONMENT, OR THE DELIVERY TO IT — one row per failure kind in this family.
// See `../failure-kind.ts` for how the four families become `TaskFailureKind`.

import { TaskHoldReason } from '../hold';
import { REPO_ENV_DOWN_HOLD_MS } from '../repo-env-fault';
import {
  BabysitRoundCharge,
  BOX_BASELINE_RED_BACKOFF_BASE_MS,
  BOX_BASELINE_RED_BACKOFF_CAP_MS,
  charged,
  type FailureKindRow,
  platform,
  TASK_MAX_ATTEMPTS,
  TaskExhaustionAction,
  uncharged,
  wire,
} from './row';

/**
 * THE BOX, ITS ENVIRONMENT, OR THE DELIVERY TO IT — the machine could not run, check out, provision, decode or be reached.
 *
 * Each row is the WHOLE decision for its kind — who writes it, what it costs, whether it
 * parks, whose fault it is and what a reader is told. The doc on each key is the kind's
 * own docblock followed by the reasoning that used to sit beside its entry in each of the
 * per-concern tables (`TASK_FAILURE_PRODUCER`, `TASK_FAILURE_BUDGET`, the box-local set,
 * the re-pend hold, `BABYSIT_ROUND_CHARGE`), moved verbatim.
 */
export const BOX_FAILURE_KINDS = {
  /**
   * The stuck-task reaper re-pended a box-held task that went silent (status + run-event silence),
   * or the review stall bound superseded a box-held review that made no progress
   * (`supersedeStalledReviewTask`: nothing but waiting marks, or a run past its wall time).
   *
   * Babysit round:
   * The box went silent and the reaper took the task back.
   *
   * Box-at-fault:
   * The task went silent and the reaper re-pended it WHILE THE BOX KEPT
   * HEARTBEATING — a box that stopped answering is `box_offline`'s job, and
   * the evaluator refuses a box the DB no longer calls online. What is left
   * is literally "the adapter broke while the heartbeat didn't".
   */
  StuckSilent: {
    value: 'stuck_silent',
    ordinal: 4,
    producer: platform(
      'packages/db/src/queries/tasks.ts#rependStuckTask',
      'packages/db/src/queries/tasks/review-expiry.ts#supersedeStalledReviewTask',
    ),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert: 'The task went silent past its allowed window and was stopped.',
      unclaimed:
        'No box claimed this — the silence window would have stopped it before the task produced any work.',
      ran: 'The task went silent past its allowed window and was stopped.',
    },
  },
  /**
   * The box could not CHECK OUT the repo: the clone ref is unreachable, the
   * registry is down, or auth was rejected at clone-time. Split out of
   * {@link RunnerError} by slice 12 so this population stops being a free-text
   * guess: today every clone failure becomes `runner_error` (the wire carries
   * a free-text `message` and no machine `code`), which buries a real "the box
   * cannot reach this host" signal under adapter timeouts and OOM kills alike.
   *
   * Budgeted to ONE attempt (#2572): one of the six kinds the issue classifies
   * as deterministic — a same-box retry cannot succeed — with revert-the-map as
   * the rollback if the post-deploy terminal-rate read shows a retry-recovering
   * population. Produced by the wire `code` map ({@link TASK_FAILURE_WIRE_CODE})
   * at every lane that can be handed no tree — THREE throw sites, not one, and the
   * enumeration with the reason each is or is not reachable is
   * {@link TASK_FAILURE_PRODUCER}'s.
   *
   * Producer:
   * EVERY LANE THAT CAN BE HANDED NO TREE, stated as that property rather than as a
   * count: the env-resolution refusal (`resolveRepoRootFromEnv` could not NAME a
   * checkout), the native team lane's own refusal to run a coordinator or an editor
   * with no repo root, and the built-in harness's refusal to confine a model's tools
   * to nothing. Same remedy — provision the box's checkout — and each was undeclared
   * until something went looking: the second when the reverse scan landed, the third
   * when the residual was decomposed by message (#4181).
   *
   * THE THIRD IS A BACKSTOP THAT FIRES. `coding.ts` refuses earlier and better (it can
   * name `DZ_RUNNER_ORG` / `DZ_REPO_ROOT`), which is why the harness arm looks
   * unreachable and is not: prod carried 6 uncoded `error` events from it in the 30
   * days to 2026-09-16. `apps/runner/src/modes/scout/survey/native.ts#NativeScoutNoCheckoutError` is the
   * fourth no-checkout refusal and is deliberately NOT here — `survey-seam.ts` CATCHES
   * it and degrades to an audited `scout_survey_failed`, so it never reaches `loop.ts`
   * and a wire code there could never be read. An unreachable producer is the `git_auth`
   * defect this table exists to prevent, so the absence is the honest entry.
   *
   * Budget:
   * From the old table's group header "slice 12, tightened by #2572":
   * The six kinds added by slice 12 shipped budgeted IDENTICALLY to
   * {@link RunnerError} — the contract half (closed vocabulary + wire `code`
   * map) shipped no producer, so every kind was unreachable until the producer
   * slice landed and its own budget was deferred to the evidence (#2572 is
   * that slice). The issue classifies all six as DETERMINISTIC — a same-box
   * retry cannot succeed: a box that cannot name its checkout still cannot
   * name it 90 seconds later, a tree on the wrong branch is still on it, a run
   * that finished without opening a PR will finish the same way — so attempts
   * two and three are paid box time guaranteed to end where attempt one ended.
   *
   * THE EVIDENCE, measured on prod 2026-08-31 over the 3 days after the
   * producer landed (2026-08-29): `tool_unavailable` 247 and `git_auth` 64,
   * populations that used to be free-text `runner_error`. `checkout_unavailable`
   * 0 and `tree_diverged` 0 — zeros that are EVIDENCE, not emptiness: they were
   * the two largest clusters before their causes were fixed (674 tree-divergence
   * events, 416 no-checkout), and they are budgeted anyway so a recurrence
   * terminates on the first occurrence instead of re-running three times.
   *
   * Rollback for this entire tightening is reverting THIS map: misjudging a
   * transient class as deterministic kills a legitimate retry, and the fix must
   * stay a one-commit flip. The post-deploy catch is the terminal-rate read at
   * comparable volume (`scripts/task-failure-report.ts`): a class whose terminal
   * rate rose was transient and was misjudged — flip it back. The two controls
   * this change must NOT touch: {@link ProviderRateLimit} stays `Uncharged`
   * (an account condition needs a hold, never a lower cap — the slice-11
   * invariant) and {@link RunnerError} keeps the full ladder (the bucket every
   * unrecognised code falls into, so tightening it would tighten everything).
   *
   * Babysit round:
   * The box could not check the repo out — the work never started.
   *
   * Box-at-fault:
   * Its three documented causes are all OFF the box — an unreachable ref, a
   * registry outage, clone-time auth refused. A genuinely box-side clone
   * failure (no disk, no DNS, no `git`) surfaces as `runner_error` or
   * `tool_unavailable`, which already page.
   */
  CheckoutUnavailable: {
    value: 'checkout_unavailable',
    ordinal: 7,
    producer: wire(
      'apps/runner/src/checkout.ts#CheckoutUnavailableError',
      'apps/runner/src/modes/worker/team/native.ts#NativeTeamNoCheckoutError',
      'apps/runner/src/adapter/index.ts#NativeHarnessNoCheckoutError',
    ),
    budget: charged(1, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The box could not check the repository out — the ref was unreachable, or clone-time auth was rejected.',
      unclaimed: 'No box claimed this — the ref was unreachable, or clone-time auth was rejected.',
      ran: 'The box could not check the repository out — the ref was unreachable, or clone-time auth was rejected.',
    },
  },
  /**
   * A tool the task requires is not present on the box — a CLI the agent
   * expected to call, a runner binary the lane depends on, or a configured
   * backend the wire failed to reach. Distinct from {@link RunnerError} because
   * the cause is provisioning, not a run-time fault.
   *
   * TWO PRODUCERS, and what they share is the OPERATOR MOVE — provision the box —
   * which is what makes this row's single-attempt budget honest. It used to also
   * carry the team lane's `not_runnable` and `preconditions_unmet` no-verdict
   * reasons; those are {@link GateUnrunnable} now, with the full ladder, because a
   * gate that could not run HERE is transient and this kind is not. #3164 left the
   * structured-output `stray` arm (the model called a tool it was NEVER GIVEN) and
   * the harness refusal below, whose declaration the reverse scan could not reach
   * until it learned to follow a constant: `task.test.ts`'s sweep now reports this
   * code against `harness/config.ts`, and this row names the class that stamps it.
   *
   * Budgeted to ONE attempt (#2572): one of the six kinds that issue classifies
   * as deterministic — a same-box retry cannot succeed, and for both producers it
   * genuinely cannot, because no re-run gives a model a tool it was never handed
   * and no re-run fills in `/etc/dz-runner/env`. The rollback #2572 named for the
   * no-verdict half — revert the map — was TAKEN in #3164, on the read it asked
   * for: 15 of the 27 tasks that reached this kind through the gate seam died on
   * their first and only attempt, against 1 of 77 through the structured-output
   * seam.
   * Produced by the wire `code` map ({@link TASK_FAILURE_WIRE_CODE}).
   *
   * Producer:
   * TWO ARMS, both of them PROVISIONING.
   *
   * The structured-output seam's `stray` arm: a TOOL THE MODEL WAS NEVER GIVEN.
   * That seam's other arm — a tool the model HAS, called with arguments that fail
   * the schema — is {@link TaskFailureKind.ProviderNonAnswer}, because nothing is
   * missing there and provisioning the box fixes nothing.
   *
   * And the harness refusal: `NativeHarnessConfigError`
   * (`apps/runner/src/harness/config.ts`), raised PER CLAIM when the box selected
   * the built-in harness without configuring it — no `DZ_MODEL_OVERRIDE`, both wires
   * credentialed at once, or no inference credential at all. Every one of those is
   * fixed in `/etc/dz-runner/env` and by nothing in the diff, which is exactly the
   * reason this kind exists and the reason a re-run on the same box buys nothing.
   *
   * THIS LIST USED TO HOLD THREE, and the pair #3164 split is why it is worth saying
   * which is which: the team lane's unrunnable-gate error stamped this code too, and
   * the budget is keyed by KIND, so one number had to serve two producers whose
   * retry semantics are opposite. The operator move really was the same for both
   * (provision the box / the tool) and that is exactly what made the conflation look
   * harmless — but a model calling a phantom tool cannot be helped by a redelivery
   * and a gate that could not run here routinely is. See
   * {@link TaskFailureKind.GateUnrunnable}. The harness arm joins the DETERMINISTIC
   * half on that same test, which is why it is named here rather than beside it.
   *
   * Babysit round:
   * The model called a tool it was never given; or the box was never configured to
   * run one at all.
   *
   * Box-at-fault:
   * A tool this row's producers need is missing FROM THE BOX — a provisioning fault on one
   * machine. NOT exit 127: since #3164 the repo gate's 126/127 is {@link GateUnrunnable}'s.
   */
  ToolUnavailable: {
    value: 'tool_unavailable',
    ordinal: 13,
    producer: wire(
      'apps/runner/src/structured-object.ts#structuredObject',
      'apps/runner/src/harness/config.ts#NativeHarnessConfigError',
    ),
    budget: charged(1, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The box could not RUN the command at all — a tool it needs is missing from the box. Nothing is wrong with the change; the box needs provisioning.',
      unclaimed: 'No box claimed this — every online box was missing the tool this task needs.',
      ran: 'The box could not run the command at all — a tool it needs is missing from the box. Nothing is wrong with the change; the box needs provisioning.',
    },
  },
  /**
   * The repo's own gate REACHED NO VERDICT because this box could not support it
   * — it could not execute the gate at all (126/127) or the gate refused to grade
   * for want of a resource it cannot reach (75, `EX_TEMPFAIL`). The change was
   * never judged, and nothing here says it is wrong.
   *
   * SPLIT OUT OF {@link ToolUnavailable} (#3164), and that kind's own docblock
   * asked for this by name. #2572 budgeted `tool_unavailable` to ONE attempt as a
   * deterministic failure, noted that the two no-verdict reasons it also carried
   * were "the part of the six a redelivery CAN clear — a heterogeneous fleet holds
   * boxes that do have the tool", and named the rollback as REVERTING THE MAP once
   * a post-deploy read showed a retry-recovering population.
   *
   * THAT READ IS IN. Prod, 30 days to 2026-09-04: 62 `no_verdict` `team_verify`
   * events over 49 tasks, and of the 27 that ended `tool_unavailable` from THIS
   * producer, **15 died on their first and only attempt** — against 1 of 77 from
   * the structured-output producer. Meanwhile every one of the ten boxes that
   * recorded a `no_verdict` for `developerz-ai/developerz.ai` also recorded a REAL
   * verdict for the same repo in the same window: the condition is transient and
   * box-local, so the second box is exactly the thing that clears it, and it was
   * the one population never given one.
   *
   * WHY A NEW KIND RATHER THAN A DIFFERENT NUMBER. The budget is keyed by kind, so
   * one entry had to serve two producers whose retry semantics are opposite: a
   * model calling a tool it was NEVER GIVEN ({@link ToolUnavailable}, where a
   * re-run provisions nothing) and a gate that could not run HERE (this kind,
   * where another box routinely can). Passing a `maxAttempts` override at the call
   * site is refused by `failureIsTerminalSql`'s own contract — the bound lives in
   * one place — so it is not the cheaper option, only the worse one.
   *
   * Budgeted {@link TASK_MAX_ATTEMPTS} ({@link TASK_FAILURE_BUDGET}): the existing
   * ladder IS the bound, so this needs no new one.
   *
   * WHAT A RETRY BUYS, STATED EXACTLY. `failOrRetryTask` clears `runner_id` and
   * re-pends, so the task becomes ELIGIBLE for any capable box — including the one
   * that just failed it, because `claimNextReady` carries no previous-runner
   * exclusion. The value does not rest on landing elsewhere and must not be
   * described as if it did: the measurement is that the condition is TRANSIENT on
   * a given box, since every box that recorded a no-verdict for that repo also
   * recorded a real verdict for it in the same window. A redelivery has a real
   * chance wherever it lands.
   *
   * Produced by the wire `code` map ({@link TASK_FAILURE_WIRE_CODE}).
   *
   * Producer:
   * The other half of #3164's split: this kind exists so the gate's no-verdict stops
   * sharing a budget with a model calling a tool it was never given.
   *
   * SIX PRODUCERS, and they are THREE DIFFERENT FACTS that share one kind because they
   * share one remedy — another box:
   *
   *   COULD NOT GRADE. `TeamVerifyNoVerdictError` (the team lane's verify step, through
   *   `NO_VERDICT_FAILURE_CODE`) and `reportEditVerdict` (the native lane, when the gate
   *   refused to grade twice in a row and latched, #17). A verdict never arrived.
   *
   *   COULD NOT SET THE BOX UP. `TeamRepoSetupError` (`./bin/setup` failed on this box)
   *   and `TeamCommitHookToolchainError` (the commit-hook toolchain would not run here).
   *   Nothing was graded because nothing could be BUILT — a fact about the box, not
   *   about the gate, and the one that actually kills tasks: a leftover container
   *   holding tesote.ai's port 6382 failed `./bin/setup` and killed the task before any
   *   agent ran.
   *
   *   COULD NOT RUN THE REPOSITORY'S OWN PRE-PUSH GUARD. The classification in
   *   `apps/runner/src/modes/worker/push-hook-refusal.ts` (`gateExitMeaning` +
   *   `classifyTailCause`, applied by `pushHookRefusal`) grades a pre-push hook that
   *   exited 75/126/127, died on a signal, or printed a box-fault tail as this kind:
   *   the guard reached NO verdict, which is this kind's whole definition. A hook that
   *   RAN and said no is {@link VerifyFailed}'s (`./tree.ts`).
   *
   * The retry semantics the #3164 split bought hold for all six: every one means "this
   * box could not do it", which a redelivery onto a DIFFERENT box can genuinely fix. It
   * is the SAME box that cannot: since #4505 this kind is box-local, so the re-pend
   * excludes the box that produced it for 30 minutes rather than handing it straight
   * back the work it just proved it cannot do.
   *
   * THE TABLE IS SYMBOL-GRANULAR AND ITS REVERSE GUARD IS NOT, which is how two of the
   * first four went unnamed for as long as they did. `task.test.ts`'s reverse check scans
   * the tree for `code = TaskFailureKind.X` and asks whether the FILE is declared; that
   * file was already declared by `TeamVerifyNoVerdictError`, so its two siblings in the
   * same module were invisible to it. A cheap fix exists — anchor that scan on the
   * enclosing `class … extends Error` and reconcile `path#Symbol` rather than `path` — and
   * until it lands, a second producer added beside an existing one in an already-named file
   * is still unguarded. Read this table as complete only that far.
   * Beside `apps/runner/src/modes/worker/tree-setup.ts#RunTreeSetupError`: one more
   * producer, same "could not set the box up" fact as `TeamRepoSetupError` and
   * `TeamCommitHookToolchainError`: the single-adapter lane's run-tree setup (`./bin/setup`
   * equivalent for that lane) failing before the gate ever got to grade
   * anything (#4854).
   *
   * Budget:
   * #3164: CHARGED with the FULL ladder, which is the opposite of `ToolUnavailable`'s
   * single attempt and is the whole point of the split. A gate that could not run on THIS
   * box is TRANSIENT — measured, every box that produced a no-verdict for that
   * repo also produced a real verdict for it in the same window — so a redelivery
   * has a real chance, wherever the assigner puts it.
   *
   * #3164 read "deliberately NOT it lands on a different box", on the premise
   * that the condition never outlives the attempt. 2026-09-19 falsified that:
   * this kind is BOX-LOCAL, see {@link taskFailureBoxLocal}, so the re-pend now
   * excludes the box that produced it. The ladder is unchanged — the exclusion
   * decides WHERE attempt two runs, never how many there are.
   *
   * On exhaustion: HOLD, not fail (2026-09-29). A spent ladder here is three boxes
   * that could not grade the change, which is a fact about the FLEET: prod's 30 days to
   * 2026-09-29 ended 158 tasks `failed gate_unrunnable`, 148 with no other error across
   * their runs, and cascade-failed 13 dependents. `tsk_1be837ae…` refused on `cargo-deny
   * not found` on every box. Failing the task kills
   * its dependents for a tool nobody installed. So the failure write holds it on
   * `env_fault_exhausted` with a blocker carrying the gate's refusal, and `task
   * redispatch` releases it once the box is provisioned (`taskFailureHoldsOnExhaustion`).
   *
   * Babysit round:
   * The repo's gate could not RUN on this box — no verdict on the change (#3164).
   *
   * Box-at-fault:
   * The repo's own gate could not RUN here (#3164) — it never executed, or it
   * refused to grade for want of something this box cannot reach. A provisioning
   * fault on one machine, which is exactly what this page is, and the same answer
   * its sibling `ToolUnavailable` gives. It is NOT `VerifyFailed`:
   * that is the gate running and saying no, on the customer's code.
   *
   * Alert sentence:
   * Split from `ToolUnavailable` (#3164). Same operator move — provision the box —
   * but a DIFFERENT budget: this one gets the full ladder because the condition
   * is transient, and the measured fleet grades the same repo on boxes that
   * failed it an hour earlier. The sentence now says "another box" and MEANS
   * it: the kind is box-local (`taskFailureBoxLocal`), so the re-pend excludes
   * the box that produced it for BOX_FAULT_EXCLUSION_MINUTES. Until 2026-09-19
   * it did not, and a box whose leftover container held the repo's port failed
   * every attempt it could reclaim.
   *
   * Reason sentence:
   * This row's copy of the GATE-REFUSAL sentence family (read by `failureReasonSentence`, `apps/api/src/routes/tasks.ts`): the repository's own
   * verify command reached no verdict. `gate_unrunnable` moved here verbatim when
   * #5077 added the second kind, which differs on the one fact a reader acts on — whose fault.
   */
  GateUnrunnable: {
    value: 'gate_unrunnable',
    ordinal: 14,
    producer: wire(
      'apps/runner/src/modes/worker/team/session/errors.ts#TeamVerifyNoVerdictError',
      'apps/runner/src/modes/worker/team/session/errors.ts#TeamRepoSetupError',
      'apps/runner/src/modes/worker/team/session/errors.ts#TeamCommitHookToolchainError',
      'apps/runner/src/harness/check-refusal.ts#reportEditVerdict',
      'apps/runner/src/modes/worker/tree-setup.ts#RunTreeSetupError',
      // A SIXTH and SEVENTH (2026-11-01): the repository's own PRE-PUSH hook, replayed
      // (`apps/runner/src/modes/worker/push-hook-refusal.ts`), refused a babysit push
      // WITHOUT grading it — exit 75 / 126 / 127 / a signal, or a tail whose last
      // recognised line is a box fault (the measured one: `ensure-deps` refusing a box
      // whose bun is not the `.bun-version` pin). The follow-up land's `push_hook_unrunnable`
      // arm and the conflict lane's push. The same fact as a gate that refused to grade,
      // and the same remedy: another box. Box-local, so the re-pend hops.
      'apps/runner/src/modes/worker/followup-land-error.ts#FOLLOWUP_LAND_WIRE_CODE',
      'apps/runner/src/modes/worker/conflict-merge-error.ts#PUSH_HOOK_UNRUNNABLE',
    ),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Hold),
    park: null,
    boxLocal: true,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The repository’s verify command never reached a VERDICT on this box — it could not execute, or it refused to grade for want of something unreachable from here. The change was never judged; it is re-queued on another box, and this one is held off it for 30 minutes. Once every attempt has ended this way the task is HELD, not failed, with a blocker naming the refusal.',
      unclaimed: 'No box claimed this — no online box could run this repository’s verify command.',
      ran: 'The repository’s verify command never reached a verdict on this box — it could not execute, or it refused to grade because something it needs is unreachable from here. Nothing is wrong with the change, and it was never judged; it is re-queued on another box, and this one is held off it for 30 minutes. Once every attempt has ended this way the task is HELD, not failed — its dependents wait — with a blocker naming the refusal; fix the box, then `task redispatch` runs it again with fresh attempts.',
    },
  },
  /**
   * The repo's own gate refused the work AND THE BASE IT WAS CUT FROM TOO — the same
   * gate, re-run against the base commit without the diff on the same box, exited
   * non-zero, so the box cannot judge ANY change. Measured (#3035): task
   * `tsk_c5fd0c644ac24350b9c6231d42e87465` (issue #2561) touched exactly two files
   * under `packages/agent/`, and its run `run_4242f0d039f644349d20757c4d0514cd`
   * (2026-09-02T17:07:51Z) died on seven failures NOT ONE of which was in
   * `packages/agent/` — a TS2322 from the box holding two copies of the `ai` package
   * side by side (`ai@7.0.64` and `ai@6.0.188`), two 60s timeouts in
   * `apps/runner/src/modes/worker/task-tree.wiring.test.ts`, three more in files the
   * diff never touched. That is a node_modules state fault on the box, graded as the
   * diff's own redness and charged an attempt.
   *
   * WHY NOT AN EXISTING MEMBER, each of which is wrong in a named way:
   *   {@link VerifyFailed}      — "the gate RUNNING and refusing red work" — is false
   *                              here: the gate's redness is a property of the base +
   *                              the box, so it never judged THIS diff at all.
   *   {@link GateUnrunnable}    — a gate that could not execute (126/127) or refused
   *                              to grade (75). Here the gate EXECUTED to its own
   *                              exit on both runs; nothing was missing, and the
   *                              remedy is not "provision a tool" but "this box's
   *                              installed tree is not what the lockfile says".
   *   {@link RunnerError}       — the charged residual. Charged is the whole defect:
   *                              the attempt counter bounds *"the fix didn't work"*,
   *                              and no judgement of the fix ever happened.
   *
   * Budgeted {@link TaskBudgetKind.Uncharged} — the same verdict
   * {@link ProviderRateLimit} gets, for the same reason: the failure was not the
   * task's, so it spends nothing. THE PACER THAT MAKES `Uncharged` SAFE IS #3094
   * (re-queue to a DIFFERENT box, blocked by this kind's own issue) and it has not
   * landed: until it does, an uncharged re-queue may revisit the box that already
   * proved it cannot judge. That is the narrowing's explicit boundary — this issue
   * ends at the typed kind and the unspent attempt (#3035, narrowed 2026-09-03).
   *
   * Produced at the team lane's red arm, only after the same gate has been re-run
   * against the base WITHOUT the diff and refused it too. A base that is clean, or
   * that reaches no verdict of its own, keeps {@link VerifyFailed} — the box is
   * never acquitted by missing evidence.
   *
   * Producer:
   * The red arm's SECOND question (#3035): thrown only after the same gate has been
   * re-run against the base commit WITHOUT the diff on the same box and refused that
   * too. A clean base, or one that reaches no verdict of its own, keeps
   * `verify_failed` — the box is never acquitted by missing evidence — so this
   * producer and `VerifyFailed`'s (`./tree.ts`) are the two arms of ONE red verdict, split by what
   * the BASE said.
   *
   * Budget:
   * NOT CHARGED (#3035) — the second `Uncharged` kind after `provider_rate_limit`,
   * and for the same reason: the attempt counter bounds "the fix didn't work", and a
   * box whose BASE is already red never judged the fix at all. Measured on
   * `tsk_c5fd0c644ac24350b9c6231d42e87465`: a two-file diff under `packages/agent/`
   * spent one of three attempts on seven failures in `scripts/` and `apps/runner/`
   * caused by the box holding two copies of the `ai` package.
   *
   * WHAT MAKES THIS SAFE IS #3094, IN TWO HALVES. `Uncharged` re-enters the queue, and
   * a redelivery that revisits THIS box re-runs a full paid team session to rediscover
   * the same red base. So:
   *
   *  - THE EXCLUSION. The refusing box may not re-claim the task for
   *    {@link BOX_FAULT_EXCLUSION_MINUTES}; any other box may, and the row carries
   *    `TaskHoldReason.BoxFaultExcluded` while it waits. It lapses on a clock, so a
   *    task whose only capable box refused it is delayed, never stranded.
   *  - THE BOUND IS A PARK, NOT A COUNT (F7, 2026-10-19). Every re-pend parks on
   *    `TaskHoldReason.RepoEnvFault` for a window that doubles per refusal in a row, from
   *    `BOX_BASELINE_RED_BACKOFF_BASE_MS` to `BOX_BASELINE_RED_BACKOFF_CAP_MS` (`./row`).
   *    THE FAILURE IT PREVENTS: a task on a repo whose own default branch is red re-pends
   *    at zero attempts spent while every box is red on that base, so no exclusion rotates
   *    it onto a green one (7 days on developerz-ai, before the cap: 590 refusals over 105
   *    tasks), each refusal a full paid run.
   *
   *    The `maxConsecutive: 3` that stood here ended the task instead
   *    (`refusals_exhausted`), and a red base is not the task's fault: 26 tesote.com tasks
   *    ended `failed box_baseline_red` on 2026-10-05..07, the tasks cut to fix that base
   *    among the rows a red base can fail. Uncapped, the park paces the loop and the base
   *    going green is what ends it.
   *
   *    WHY `repo_env_fault`: of the windowed holds (`tasks_hold_until_ck`) it is the one
   *    that says "the repository's own environment, every box, not the change". Its label
   *    names `bin/setup`, which is imprecise here; an exact `base_red` member needs a
   *    migration of both hold CHECKs, a dashboard label and every total map over the enum.
   *    The repo-env sweep may RELEASE such a row early (a probe, a cleared fault on the same
   *    repo), which costs one early re-try and never strands it. A push to the default
   *    branch releases it (the worker's default-branch push release); the backoff, capped
   *    at `BOX_BASELINE_RED_BACKOFF_CAP_MS`, is the fallback for a release that never comes.
   *
   * Babysit round:
   * Old table group: the box, or the delivery to it.
   * The base was already red on this box; the round could not judge anything (#3035).
   *
   * Box-at-fault:
   * The box's own checkout fails the verify command BEFORE the diff is even
   * applied. The diff cannot have caused it; the box's environment, snapshot,
   * or pre-existing tree state can. This is exactly the kind of per-machine
   * defect this page exists for — every task the next box runs is fine, and
   * every task THIS box runs again hits the same red baseline.
   *
   * Alert sentence:
   * Named from its first diagnosis (a box's broken `node_modules`), but the
   * measured population is mostly the repository's own default branch failing
   * verify with the diff removed (`baseline-red.ts` `EXCLUDED_CODE_ALARMS`), so
   * the sentence sends the reader to the branch first and the box second. It
   * states the FIX and not "then retry this task": this same sentence rides a
   * terminal alert too, where the row is closed and the reader who waits for the
   * retry it was promised is the failure the wording would cause.
   *
   * Reason sentence:
   * BOTH arms carry the park rule, and that is not duplication — it is the one
   * thing `attempt` cannot tell this mapping. A refusal never charges an attempt,
   * so `attempt` here counts CHARGED failures that happened earlier under some
   * other kind: a row that burned an attempt on `verify_failed` and then took a
   * refusal reads the `ran` arm. Gating the rule on `attempt === 0` would hide it
   * from exactly the row it explains. Until F7 (2026-10-19) the rule was a STOP rule
   * ("stops after repeated refusals in a row"); the park replaced the cap, and a
   * sentence promising an end the row no longer reaches is the defect this copy
   * exists to avoid.
   */
  BoxBaselineRed: {
    value: 'box_baseline_red',
    ordinal: 21,
    producer: wire(
      'apps/runner/src/modes/worker/team/session/errors.ts#TeamVerifyBaselineRedError',
    ),
    budget: uncharged(),
    park: {
      reason: TaskHoldReason.RepoEnvFault,
      backstopMs: BOX_BASELINE_RED_BACKOFF_BASE_MS,
      honorsStatedReset: false,
      backoffCapMs: BOX_BASELINE_RED_BACKOFF_CAP_MS,
    },
    boxLocal: true,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: true,
    copy: {
      alert:
        "The repository's verify command failed on the base commit with the diff removed, so the change was never graded and no attempt was charged. That is almost always the repository's default branch being red — if every refusal came from one box, that box's environment. Make the default branch pass verify; a pending task retries on its own, a failed one can be retried.",
      unclaimed:
        'No box graded the diff — the repository’s verify command failed on the base commit with the diff removed, so no attempt was charged. That is almost always the repository’s default branch being red — if every refusal came from one box, that box’s environment. The task is parked, not failed: it resumes when the default branch is pushed, or on a wait that grows while the base stays red — make the default branch pass verify and push it; a task failed this way before can be retried.',
      ran: 'The repository’s verify command failed on the base commit with the diff removed, so the change was never graded and no attempt was charged — the attempts it does carry were spent by earlier failures of some other kind. That is almost always the repository’s default branch being red — if every refusal came from one box, that box’s environment. The task is parked, not failed: it resumes when the default branch is pushed, or on a wait that grows while the base stays red — make the default branch pass verify and push it; a task failed this way before can be retried.',
    },
  },
  /**
   * THE SURVEY SEAM IS ABSENT on this box — `deps.survey === undefined`, the
   * operator's `DZ_SCOUT_MAX_ROUNDS=0`, or the box has no model configured to
   * call the survey at all. The fence promises the model a brief; a box that
   * cannot produce one refused the call before any plan ran, on the same shape
   * `ScoutGroundingMissingError` raises for every other missing precondition
   * (the `survey_unwired` arm of `scoutGroundingFault`,
   * `apps/runner/src/modes/scout/executor.ts`), split out of the old
   * `survey_brief` arm alongside the `tree_unproven` half (which folds into
   * {@link ScoutTreeUnproven}).
   *
   * MEASURED, 2026-09-04: 31 of 685 `scout_survey` rows rejected at the
   * precondition for an absent survey seam — the population today's
   * `ScoutGroundingMissingError` throws for the `survey_unwired` arm. Before
   * #3402 the wire `code` map `SCOUT_GROUNDING_WIRE_CODE` folded every one of
   * them into `'tool_unavailable'`, which carries a single-attempt `Charged`
   * budget — so the population burned its lone attempt on a fault a fresh box
   * may clear, and a misconfigured box's first fault after a redeploy went
   * straight to `failed` under a budget that was wrong for it. The split is
   * SILENT on the column (`tool_unavailable` WAS a `TaskFailureKind`), but the
   * ladder drop is the same shape `ScoutTreeUnproven` documents: a population
   * reaching a kind whose budget did not match what redelivery can recover.
   * `scout_survey_unwired` is what the wire `code` map now stamps from the same
   * `ScoutGroundingMissingError` site, so the population ends up counted
   * under its cause rather than under a budget that was never its own.
   *
   * Three siblings, each distinct from this one in a named way — the borders
   * are the whole point of a fourth scout-shaped member:
   *   {@link ScoutTreeUnproven} — the checkout is on disk but NOT the proven
   *                              default branch at head; the survey SEAM ran
   *                              and the survey's tree check refused the
   *                              cloned HEAD. Survey seam WIRED — this kind is
   *                              what is raised BEFORE the seam ran and only
   *                              when no seam exists at all. Distinct because
   *                              the absent input here is the runner itself,
   *                              not the tree it reads.
   *   {@link ToolUnavailable}   — a tool the MODEL was never given (the
   *                              structured-output `stray` arm); provisioning
   *                              the box fixes nothing. Different because the
   *                              absent input here is a survey-side runner,
   *                              not a model-side tool: a fresh box may wire
   *                              one and the ladder buys that.
   *   {@link GateUnrunnable}    — a gate that could not RUN HERE (a 126/127
   *                              exit, or a 75 `EX_TEMPFAIL`); same box-state
   *                              posture, budgeted identically because another
   *                              box routinely clears it. Distinct because the
   *                              gate IS mounted and the survey runner is not.
   *
   * Budgeted identically to {@link ScoutTreeUnproven}: `Charged`, the standard
   * {@link TASK_MAX_ATTEMPTS} ladder, exhausted `Fail`. The absent-survey-seam
   * fault is BOX-LOCAL — a different box may have a survey runner configured —
   * so a redelivery has a real chance wherever `claimNextReady` puts it, exactly
   * as #3164 argued for the gate arm. `Uncharged` would re-queue on a BYOK
   * credential that DID spend attempts, and that is the #2567 loop reached
   * through the survey door — the operator move is provision a model or
   * re-enable the survey on a capable box, not a budget judgement.
   *
   * Produced by `apps/runner/src/modes/scout/executor.ts#ScoutGroundingMissingError`
   * at the `survey_unwired` arm of `scoutGroundingFault`; the wire `code` map
   * `SCOUT_GROUNDING_WIRE_CODE` there stamps this kind under the
   * `'scout_survey_unwired'` code.
   *
   * Producer:
   * #3402: the `survey_unwired` arm of `scoutGroundingFault` — ONE producer (the
   * `ScoutGroundingMissingError` constructor throws verbatim from
   * `scout/executor.ts`'s precondition check), and the wire `code` map
   * `SCOUT_GROUNDING_WIRE_CODE` there names this kind under
   * `'scout_survey_unwired'`. The audit chain carries no separate event for
   * this arm — the throw site is the only producer, and the `error` event the
   * loop forwards already names the cause via the wire `code`.
   *
   * Budget:
   * #3402: same posture as `scout_tree_unproven` — the full `TASK_MAX_ATTEMPTS`
   * ladder, exhausted `Fail`. The absent-survey-seam fault is BOX-LOCAL: a
   * different box may have a survey runner configured (model wired in,
   * `DZ_SCOUT_MAX_ROUNDS > 0`), so a redelivery has a real chance wherever
   * `claimNextReady` puts it. This row's docblock draws the sibling borders
   * with `scout_tree_unproven` / `tool_unavailable` / `gate_unrunnable`.
   *
   * Babysit round:
   * The box has no survey seam wired — box config; not a babysit producer.
   *
   * Box-at-fault:
   * #3402: the survey seam was never wired on this box. Same posture as
   * `ScoutTreeUnproven` and `GateUnrunnable` — transient across replicas, a
   * different box may have a survey runner configured — so this is NOT a
   * box fault and does not belong on the secret-rotation paging path. Pinned
   * explicitly so a future edit that buckets it with `tool_unavailable`
   * fails HERE rather than paging an operator to provision a tool the box
   * already has.
   *
   * Alert sentence:
   * Split from `tool_unavailable` and from the prior `survey_brief` arm
   * (#3402). The precondition refused the run because no survey runner is
   * wired on this box at all — a transient box-local fault that carries the
   * full ladder rather than `tool_unavailable`'s deterministic one.
   *
   * Reason sentence:
   * Split from the prior `survey_brief` arm (#3281) and from
   * `tool_unavailable` (#3402). The precondition refused the run because no
   * survey runner is wired on this box at all (`deps.survey === undefined`,
   * the box can name no model for the scout lane). A
   * transient box-local fault — a different box may have a survey runner
   * configured — so it carries the full ladder rather than
   * `tool_unavailable`'s single-attempt budget.
   */
  ScoutSurveyUnwired: {
    value: 'scout_survey_unwired',
    ordinal: 31,
    producer: wire('apps/runner/src/modes/scout/executor.ts#ScoutGroundingMissingError'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The scout precondition found no survey runner wired on this box, so the run never started. Set `DZ_MODEL_OVERRIDE` and an inference credential on the box; the work is re-queued and eligible for another box.',
      unclaimed:
        'No box claimed this — the scout precondition refused the run because no survey runner is wired on this box; set `DZ_MODEL_OVERRIDE` and an inference credential on the box.',
      ran: 'The scout precondition found no survey runner wired on this box, so the run never started. Set `DZ_MODEL_OVERRIDE` and an inference credential on the box; the work is re-queued and eligible for another box.',
    },
  },
  /**
   * A REPO-ONBOARDING PASS STOPPED ON THE BOX'S TREE, not on a prerequisite anybody
   * owns — `dirty_worktree` (tracked modifications the landing would fold in, or a
   * path the pass owns that the target tree overwrites) or `untracked_collision` (an
   * untracked file standing where the branch this pass lands carries one). Split out
   * of {@link OnboardingBlocked} by #4362; every OTHER blocker stays there.
   *
   * THE SPLIT IS THE WHOLE POINT, and the premise it corrects is written down in
   * {@link OnboardingBlocked}'s own docblock: it listed "clean the tree" beside "mint
   * the token" as a thing a HUMAN does. A token is an account fact and identical on
   * every box in the fleet; a dirty working tree is one box's disk, and THE PASS REPAIRS
   * IT ITSELF — step 0'' of `apps/runner/src/modes/setup/executor/run.ts` runs the
   * coding lane's `preflightTaskTree` at task start, before the pass writes a byte or
   * opens a model, so the tree the redelivery meets is the one that call cleaned. That
   * is what makes the refusal self-healing, and it is a stronger claim than the one this
   * split was first argued on ("the box's next coding task parks it for free"), which was
   * FALSE on the boxes this failure happened to — boxes then pinned `setup`, never offered
   * a coding task. The pass that refused CAN succeed on a redelivery with nothing human
   * happening in between, which is the definition this vocabulary budgets on.
   *
   * MEASURED, 2026-09-17: two tesote/tesote.ai setup tasks died terminal on this
   * shape in one day — one on an untyped `checkout -B` abort over a modified tracked
   * `.gitignore` the pass had itself appended, one on a correctly typed
   * `dirty_worktree` naming two files another task had left behind. Neither burned an
   * attempt, and neither was ever retried.
   *
   * `Uncharged` with `maxConsecutive: 3`, the budget {@link BoxBaselineRed} and
   * {@link DiskFloorUnmet} already carry, for the same reason: the pass was never
   * graded (nothing was pushed, no PR was opened, no model ran the landing), so the
   * attempt ladder — which bounds *"the fix didn't work"* — has nothing to count. The
   * bound that IS named is `maxConsecutive`, and the failure it prevents is a
   * permanently wedged box re-pending one row forever at zero attempts spent, which
   * is the failure `Uncharged` alone would have.
   *
   * NO BOX-FAULT EXCLUSION, unlike `box_baseline_red`: `failOrRetryTask`
   * (`packages/db/src/queries/tasks/lifecycle.ts`) stamps `box_fault_until` for that
   * one kind only. Stated rather than assumed, because the pacer is what makes an
   * `Uncharged` re-queue safe elsewhere; here the three-in-a-row cap is the whole
   * bound, and a box whose tree stays wedged across three redeliveries has a problem
   * a fourth would not fix either.
   *
   * Produced by the wire `code` map ({@link TASK_FAILURE_WIRE_CODE}) — the box stamps
   * it at `OnboardingBlockedError` (`apps/runner/src/modes/setup/executor/settle.ts`),
   * whose `code` is `onboardingBlockerFailureKind` over
   * {@link ONBOARDING_BLOCKER_CLEARED_BY}. One error class, one throw site, two budgets:
   * a new blocker does not compile until that table says which side of this line it
   * falls on.
   *
   * Producer:
   * #4362: the SAME mint and the SAME throw site, stamping a second code. One error
   * class, one throw, two budgets, and no second branch in the event sink to keep in
   * step. A blocker added to `SETUP_BLOCKERS` does not compile until
   * `ONBOARDING_BLOCKER_CLEARED_BY` says whether the box's tree or a human clears it,
   * which is the decision this split exists to force.
   *
   * Budget:
   * #4362 — the tree half of {@link TaskFailureKind.OnboardingBlocked}, budgeted the
   * way `box_baseline_red` and `disk_floor_unmet` are and for the same reason: the
   * pass was never GRADED. Nothing was pushed, no pull request was opened, no model
   * ran the landing — so the attempt ladder, which bounds *"the fix didn't work"*,
   * has no fix to count. What makes the re-queue worth spending is that the refusal is
   * per-box state THE PASS CLEARS ON ITS WAY BACK IN: the same executor's task-start
   * preflight parks the dirt and lands the tree on the fetched base before it writes a
   * byte, so the redelivery meets a tree this run repaired — whichever box takes it, and
   * with no other lane involved.
   *
   * `maxConsecutive` IS THE ONLY BOUND, and it names its failure rather than a bill:
   * a box whose tree stays wedged would otherwise re-pend the same row forever at
   * zero attempts spent. Three in a row trip `refusals_exhausted`, the same backstop
   * the other two `Uncharged` environmental kinds end on.
   *
   * It does NOT ride the #3094 box-fault pacer: `failOrRetryTask`
   * (`packages/db/src/queries/tasks/lifecycle.ts`) stamps `box_fault_until` for
   * `box_baseline_red` alone. Said here rather than assumed from the sibling budgets,
   * because the pacer is what makes an uncharged re-queue safe elsewhere and the cap
   * is what does it here.
   *
   * Babysit round:
   * An onboarding pass stopped on the box's dirty tree (#4362) — not a babysit producer.
   *
   * Box-at-fault:
   * #4362: the OPPOSITE side of the line from its sibling `OnboardingBlocked` (`./tree.ts`), and for the reason
   * that split them. A dirty working tree is this box's disk — the same setup task
   * on any other box does not meet it — so the machine IS the subject, exactly as it
   * is for `BoxBaselineRed` and `DiskFloorUnmet`. What bounds the page is NOT the
   * three-in-a-row cap (that bounds the task row only, and no seam between that
   * write and this gate reads it, so the FIRST refusal pages) — it is
   * `boxUnhealthyAlert`'s 60-minute dedupe window, one page per box per hour.
   *
   * Alert sentence:
   * #4362 — the same pass, the other cause, and the sentence has to say so: this one
   * needs NOBODY. It reads like `DiskFloorUnmet` because it is the same class (the
   * box's own filesystem state, the pass never graded), and it states that the row
   * retries, because the reader of `OnboardingBlocked`'s sentence was told the opposite.
   *
   * Reason sentence:
   * #4362 — repo onboarding stopped on the BOX'S TREE rather than on a prerequisite
   * anybody owns, split out of `OnboardingBlocked` (`./tree.ts`). The sentence has to be the
   * inverse of that one on the only point a reader acts on: that one says "this is
   * not retried — satisfy it and run setup again", and this one is retried without
   * anyone doing anything. Same stop rule as the environmental kinds `DiskFloorUnmet` and `ServiceUnreachable`:
   * three refusals in a row trip `refusals_exhausted`.
   */
  OnboardingTreeDirty: {
    value: 'onboarding_tree_dirty',
    ordinal: 33,
    producer: wire(
      'packages/domain/src/value-objects/task-run-event/onboarding-blocker.ts#onboardingBlockerFailureKind',
    ),
    budget: uncharged(3),
    park: null,
    boxLocal: false,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'Repo onboarding could not cut its branch: the box’s checkout carried uncommitted changes the landing would have folded in or overwritten. That is the box, not the repository — nothing was opened, no attempt was charged, and the row re-queues on its own once any box takes it with a clean tree. If it refuses repeatedly on the same box, clear that checkout by hand.',
      unclaimed:
        'No box claimed this — the last box to try repo onboarding could not cut its branch over uncommitted changes in its own checkout, so the row was turned away before dispatch.',
      ran: 'Repo onboarding could not cut its branch: the box’s checkout carried uncommitted changes the landing would have folded in or overwritten. Nothing about your repository is wrong and no attempt was charged — the row re-queues, and the next pass tidies that checkout before it starts, so it finishes itself even if that box is the only one you have. It stops after repeated refusals in a row rather than retrying forever.',
    },
  },
  /**
   * THE BOX'S PRE-TASK HYGIENE FOUND THE DISK FLOOR UNMET — the hygiene pass ran
   * its own cleanup (the package cache, the stale checkouts, the docker images)
   * and the floor was STILL not satisfied when it resampled. The diff was never
   * graded: the executor never ran, no attempt was charged, and a fresh box may
   * hold the same work without re-deriving the same refusal.
   *
   * MIRRORS {@link BoxBaselineRed}, and that is the whole reason this kind exists.
   * Before #4309 the refusal was a FREE-TEXT STRING
   * (`apps/runner/src/run-scope.ts#runBeforeHygiene` returned it) carrying no
   * `err.code`, so `classifyTaskFailure` fell through to the residual
   * `runner_error` and the rendered reason read *"no named cause claimed it"* —
   * false, since the box named the cause precisely. 44 tasks in 7 days (#4094)
   * were filed that way, and every one of them was an environmental refusal the
   * task could not have caused.
   *
   * Budgeted IDENTICALLY to {@link BoxBaselineRed} — `Uncharged` with
   * `maxConsecutive: 3` — for the same reason and against the same failure: a
   * permanently starved box would otherwise re-pend the same row forever at zero
   * attempts spent. The pacer that makes `Uncharged` safe is the SAME ONE
   * `box_baseline_red` rides (#3094): the refusing box may not re-claim the task
   * for {@link BOX_FAULT_EXCLUSION_MINUTES}; any other box may, and the row
   * carries `TaskHoldReason.BoxFaultExcluded` while it waits. The clock lapses,
   * so a task whose only capable box refused it is delayed, never stranded.
   *
   * OUT OF SCOPE here: the CLAIM-GATE half of #4094 — a box below its floor
   * still advertising capacity and winning them. That is a fleet-assigner change
   * and stays on #4094 as HITL; this kind ends at the typed refusal and the
   * unspent attempt, exactly as #3035 narrowed {@link BoxBaselineRed}.
   *
   * Produced at `apps/runner/src/run-scope.ts#DiskFloorUnmetError` — the
   * `runBeforeHygiene` before-guard throws it where the free-text refusal was
   * returned, and the runner's existing catch-all forwards `err.code` onto the
   * terminal `error` event unchanged, so the wire `code` map
   * ({@link TASK_FAILURE_WIRE_CODE}) grades the kind by enumeration exactly as
   * the other wire kinds do.
   *
   * Producer:
   * #4309 — the typed refusal `runBeforeHygiene` throws when its resample after the
   * pass's own cleanup still finds the disk floor unmet, in the same shape as
   * {@link TaskFailureKind.BoxBaselineRed}'s `TeamVerifyBaselineRedError`. The
   * wire `code` map {@link TASK_FAILURE_WIRE_CODE} stamps this kind under the
   * `'disk_floor_unmet'` code, the runner's existing catch-all forwards
   * `err.code` onto the terminal `error` event unchanged, and the classifier
   * grades it by enumeration — no free-text heuristic, no message match. One
   * throw site, one stamp, one producer; the partition that took four undeclared
   * producers to build refuses a kind that has no throw site, so adding the
   * typed error in the same PR is the only way this entry compiles.
   *
   * Budget:
   * #4309 — MIRROR OF {@link TaskFailureKind.BoxBaselineRed} on the same class
   * (environmental, the diff was never graded), kept under the same cap for the
   * same reason: the attempt counter bounds *"the fix didn't work"*, and a
   * hygiene pass that still finds the disk floor unmet never judged the fix at
   * all. Three consecutive refusals on one box trip `refusals_exhausted`, the
   * same backstop that bounds a permanently starved box from re-pending the
   * same row forever at zero attempts spent.
   *
   * The PACER that makes `Uncharged` safe is again #3094: the refusing box may
   * not re-claim the task for {@link BOX_FAULT_EXCLUSION_MINUTES}; any other
   * box may, and the row carries `TaskHoldReason.BoxFaultExcluded` while it
   * waits. A clock lapses; a task whose only capable box refused it is
   * delayed, never stranded. The claim-gate half of #4094 (a box below its
   * floor still advertising capacity and winning claims) stays on #4094 as
   * HITL — this kind ends at the typed refusal and the unspent attempt, the
   * same narrowing #3035 took on {@link BoxBaselineRed}.
   *
   * Box-local:
   * The pre-task hygiene pass left THIS box below its disk floor; a box with room runs
   * the same task. Its budget and docblock always claimed this pacer and it was never
   * here, so the uncharged re-pend went straight back to the starved box and the
   * consecutive cap ended the task on one box's disk. Measured on prod 2026-09-26,
   * tesote/tesote.ai: `tsk_813b771b…` refused three times in a row, every time by the
   * same box at 13.2 GiB free against a 20 GiB floor, then `refusals_exhausted` and
   * `dependency_failed` on four dependents — while eleven online boxes had 21-41 GiB.
   *
   * Babysit round:
   * The box's own hygiene floor refused before the run (#4309).
   *
   * Box-at-fault:
   * #4309 — the pre-task hygiene pass found the disk floor still unmet after
   * its own cleanup, so the executor never ran. A per-machine defect in the
   * same register as `BoxBaselineRed`: the diff could not have caused it
   * (the executor never even saw it), the box's own filesystem state can,
   * and every task THIS box claims next will hit the same floor. A fresh
   * box holds the same work without re-deriving the refusal, so paging the
   * refusing box is exactly the move this kind exists to drive.
   *
   * Alert sentence:
   * THE BOX'S ENVIRONMENT REFUSED THE WORK BEFORE ANY EXECUTOR RAN — no attempt charged,
   * three in a row on the row ends it, the refusing box is kept off the next attempt. Both
   * read like `BoxBaselineRed` because both are environmental refusals; the operator move
   * differs and each sentence names its own.
   *
   * - #4309 `disk_floor_unmet`: the pre-task hygiene pass found the disk floor still unmet
   *   after its own cleanup. Free disk on the box.
   * - #4332 `service_unreachable`: the repo's committed policy declares a service and the
   *   box's own run-start probe found nothing listening on it after the repo's own setup.
   *   Bring the service up on the box (or fix the setup that should have).
   * - #5000 `baseline_stash_stranded`: NOT before the executor — after it. The base probe
   *   could not keep the run's diff in the tree, so the run stopped before committing; same
   *   budget and exclusion, and the sentence says where the change is.
   *
   * Reason sentence:
   * This row's copy of the ENVIRONMENTAL sentence family (read by `failureReasonSentence`, `apps/api/src/routes/tasks.ts`): the BOX refused, not
   * the task — a per-machine fact the next box does not share, so a retry on another box is
   * the remedy rather than anything in the diff. The kinds share one
   * shape ("No box graded the diff — {cause}, so no attempt was charged. That is {whose}, not
   * the task: {remedy}. A task refused this way stops after repeated refusals in a row rather
   * than retrying forever…") and one stop rule: three CONSECUTIVE refusals of the row trip
   * `refusals_exhausted`. #5000's kind is the one whose run DID the work and was graded red: the
   * base probe lost the diff before comparing that red with the base, so its sentence says so,
   * and where the change is.
   *
   * #4309 — the pre-task hygiene pass found the disk floor still unmet after its own
   * cleanup, so the executor never ran. The same verdict `BoxBaselineRed`'s copy states,
   * for the same reason: a per-machine defect the next box does not share.
   */
  DiskFloorUnmet: {
    value: 'disk_floor_unmet',
    ordinal: 34,
    producer: wire('apps/runner/src/run-scope.ts#DiskFloorUnmetError'),
    budget: uncharged(3),
    park: null,
    boxLocal: true,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The pre-task hygiene pass found the disk floor still unmet after its own cleanup, so the executor never ran and no attempt was charged. That is almost always the box, not the task: free disk on the box (clear the package cache, prune stale checkouts, or reclaim Docker). A task refused this way stops after repeated refusals in a row rather than retrying forever.',
      unclaimed:
        'No box graded the diff — the pre-task hygiene pass found the disk floor still unmet after its own cleanup, so the executor never ran and no attempt was charged. That is almost always the box, not the task: free disk on the box (clear the package cache, prune stale checkouts, or reclaim Docker) and a pending task retries on its own. A task refused this way stops after repeated refusals in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.',
      ran: 'The pre-task hygiene pass found the disk floor still unmet after its own cleanup, so the change was never graded and no attempt was charged — the attempts it does carry were spent by earlier failures of some other kind. That is almost always the box, not the task: free disk on the box (clear the package cache, prune stale checkouts, or reclaim Docker). A task refused this way stops after repeated refusals in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.',
    },
  },
  /**
   * The box could not mint the run's memory cage — the per-run cgroup
   * (`apps/runner/src/task-cgroup.ts` `mintRunCgroup`) — so the run never started: a box
   * that has the cage never runs a task bare. Graded `runner_error` before, which charged
   * the customer an attempt for the box's own cgroupfs. Budgeted like {@link DiskFloorUnmet}
   * for its reason: no executor ran, and the next box does not share the fault.
   */
  TaskCageUnmintable: {
    value: 'task_cage_unmintable',
    ordinal: 51,
    producer: wire('apps/runner/src/task-cgroup.ts#RunCgroupMintError'),
    budget: uncharged(3),
    park: null,
    boxLocal: true,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The box could not create the run's memory cage (its per-run cgroup), so the executor never ran and no attempt was charged. That is the box, not the task: check the box's cgroup v2 delegation (the dz-runner service must own a writable cgroup subtree). A task refused this way stops after repeated refusals in a row rather than retrying forever.",
      unclaimed:
        "No box graded the diff — the box could not create the run's memory cage (its per-run cgroup), so the executor never ran and no attempt was charged. That is the box, not the task: check the box's cgroup v2 delegation and a pending task retries on its own. A task refused this way stops after repeated refusals in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.",
      ran: "The box could not create the run's memory cage (its per-run cgroup), so the change was never graded and no attempt was charged — the attempts it does carry were spent by earlier failures of some other kind. That is the box, not the task: check the box's cgroup v2 delegation (the dz-runner service must own a writable cgroup subtree). A task refused this way stops after repeated refusals in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.",
    },
  },
  /**
   * THE GATE'S INTERPRETER IS GONE FROM THIS BOX (`toolchain.gate_interpreter`, plan 101
   * B3, decided 2026-10-08). The boot fence proved an absolute, executable `bun` for the
   * daemon's principal (`apps/runner/src/harness/toolchain.ts`); this is the box degrading
   * after it — the interpreter removed or its mode changed under a running box. The gate
   * seams used to warn and spawn anyway, the repo's `bin/check` exited 127, and the run
   * was CHARGED `gate_unrunnable` for the box's own toolchain. Now both seams (the team
   * verifier and the single lane's post-edit check) refuse BEFORE the spawn.
   *
   * Budgeted like {@link DiskFloorUnmet} for its reason: no gate ran, so no fix was judged,
   * and the next box does not share the fault. Box-local (the refusing box is kept off the
   * re-pend while {@link BOX_FAULT_EXCLUSION_MINUTES} lapse) and box-blaming (an operator
   * goes to the box), three consecutive refusals trip `refusals_exhausted`.
   */
  GateInterpreterMissing: {
    value: 'gate_interpreter_missing',
    ordinal: 58,
    producer: wire(
      'apps/runner/src/harness/gate-interpreter-missing.ts#GateInterpreterMissingError',
    ),
    budget: uncharged(3),
    park: null,
    boxLocal: true,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The box could not find the interpreter the repository's gate runs under (bun, resolved for the runner's own user), so the gate never ran and no attempt was charged. That is the box, not the task: reinstall bun for the dz-runner user, or restart the runner so its boot check names what is missing. A task refused this way stops after repeated refusals in a row rather than retrying forever.",
      unclaimed:
        "No box graded the diff — the box could not find the interpreter the repository's gate runs under (bun, resolved for the runner's own user), so the gate never ran and no attempt was charged. That is the box, not the task: reinstall bun for the dz-runner user and a pending task retries on its own. A task refused this way stops after repeated refusals in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.",
      ran: "The box could not find the interpreter the repository's gate runs under (bun, resolved for the runner's own user), so the change was never graded and no attempt was charged — the attempts it does carry were spent by earlier failures of some other kind. That is the box, not the task: reinstall bun for the dz-runner user, or restart the runner so its boot check names what is missing. A task refused this way stops after repeated refusals in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.",
    },
  },
  /**
   * THE BOX RAN OUT OF DISK MID-RUN — an `ENOSPC` / "No space left on device" reached
   * the runner's catch-all after the executor started (`apps/runner/src/run-failure.ts`
   * `terminalFailure`, which stamps it on an error carrying no code of its own or only
   * the fs errno). The run-time twin of {@link DiskFloorUnmet}, which refuses BEFORE the
   * executor: same box condition, met later, so the same budget for the same reason — the
   * next box does not share this box's disk. Graded `runner_error` before (charged): a
   * Node fs error carries `code: 'ENOSPC'`, which is no wire code.
   *
   * Box-local: the re-pend keeps this box off the task, and this box's own next `before`
   * pass meets its disk floor and reclaims (`apps/runner/src/hygiene/floor-reclaim.ts`).
   * A red GATE whose tail mentions ENOSPC is NOT graded here: that text is the repo's,
   * and the gate's own `verify_failed` stands.
   */
  DiskExhausted: {
    value: 'disk_exhausted',
    ordinal: 53,
    producer: wire('apps/runner/src/run-failure.ts#terminalFailure'),
    budget: uncharged(3),
    park: null,
    boxLocal: true,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The box ran out of disk while the run was working ("No space left on device"), so the change was never graded and no attempt was charged. That is the box, not the task: the task goes to another box, and this box reclaims disk on its next run (unused Docker images, package caches, stale checkouts). A task refused this way stops after repeated refusals in a row rather than retrying forever.',
      unclaimed:
        'No box graded the diff — the box ran out of disk while the run was working ("No space left on device"), so no attempt was charged. That is the box, not the task: the task goes to another box, and this box reclaims disk on its next run (unused Docker images, package caches, stale checkouts); a pending task retries on its own. A task refused this way stops after repeated refusals in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.',
      ran: 'The box ran out of disk while the run was working ("No space left on device"), so the change was never graded and no attempt was charged — the attempts it does carry were spent by earlier failures of some other kind. That is the box, not the task: the task goes to another box, and this box reclaims disk on its next run (unused Docker images, package caches, stale checkouts). A task refused this way stops after repeated refusals in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.',
    },
  },
  /**
   * A SERVICE THE REPO'S COMMITTED POLICY DECLARES (`services:` in `.maintainer.yml`)
   * ANSWERED NOTHING ON THIS BOX'S LOOPBACK at run start, AFTER the repo's own
   * `bin/setup` had its chance to bring it up and BEFORE the survey — so no model ran
   * against a stack the gate could never have exercised (#4332).
   *
   * The box, not the task: the same policy on a box whose Redis answers runs to
   * completion, so the redelivery elsewhere has a real chance and the refusing box
   * is what must not take it again. `Uncharged` with `maxConsecutive: 3`, the budget
   * {@link BoxBaselineRed} and {@link DiskFloorUnmet} carry and for their reason: the
   * attempt ladder bounds *"the fix didn't work"*, and a session refused before its
   * survey never judged a fix. It IS box-local (`taskFailureBoxLocal`): the re-pend
   * adds the refusing box to `box_fault_runner_ids` and moves `box_fault_until`, and the row
   * carries `TaskHoldReason.BoxFaultExcluded` while the #3094 clock lapses, so a task
   * whose only capable box refused it is delayed, never stranded.
   *
   * WHY THE CLAIM GATE DID NOT PREVENT THIS: the box's `service:*` advert is a
   * union-only write fed by a historical probe success, and it never narrows
   * (#4333 gives it the way down, fed by this refusal). A box that once reached
   * Redis keeps winning claims for repos that need it after Redis is gone.
   *
   * Produced at `apps/runner/src/modes/worker/team/session/errors.ts#TeamServiceUnreachableError`,
   * thrown from the team session's run-start probe immediately after `bin/setup`;
   * the runner's catch-all forwards `err.code` onto the terminal `error` event
   * unchanged, so the wire `code` map ({@link TASK_FAILURE_WIRE_CODE}) grades it by
   * enumeration exactly as the other wire kinds are.
   *
   * Producer:
   * #4332 — the team session's run-start probe of the services the checkout's own
   * committed policy declares, thrown right after `bin/setup` and before the survey in
   * the `TeamRepoSetupError` shape. Same wire discipline as `DiskFloorUnmet`: one throw
   * site stamps the code, the catch-all forwards it, the classifier enumerates it.
   *
   * Budget:
   * #4332 — the SAME class as `OnboardingTreeDirty` and `DiskFloorUnmet` (environmental, refused before any model
   * ran, the diff never graded) under the same cap for the same reason: three
   * consecutive refusals trip `refusals_exhausted` rather than re-pending forever at
   * zero attempts spent. Box-local (`BOX_LOCAL_FAILURE_KINDS` (`../failure-budget.ts`)): the refusing box is
   * excluded for {@link BOX_FAULT_EXCLUSION_MINUTES}, which is the pacer that makes
   * `Uncharged` safe here — the box's stale `service:*` advert is what let it claim.
   *
   * Box-local:
   * #4332: a declared service answered nothing on THIS box's loopback after its own
   * `bin/setup`; the same policy on a box whose service answers runs. The claim gate
   * let the box in on a union-only advert that never narrows (#4333), so the
   * exclusion is the only thing keeping the same box from re-claiming the row.
   *
   * Babysit round:
   * A policy-declared service answered nothing on this box after its own setup (#4332).
   *
   * Box-at-fault:
   * #4332 — the repo's committed policy declares a service and THIS box's own
   * run-start probe found nothing listening on it after the repo's setup. The same
   * task on a box where the service is up runs; the box that lost its Redis is the
   * subject. Its `service:*` advert is NOT retracted: no consumer of the probe row
   * exists yet (#4333, open), so the exclusion above is the only pacer (row 09-49).
   *
   * Alert sentence:
   * THE BOX'S ENVIRONMENT REFUSED THE WORK BEFORE ANY EXECUTOR RAN — no attempt charged,
   * three in a row on the row ends it, the refusing box is kept off the next attempt. Both
   * read like `BoxBaselineRed` because both are environmental refusals; the operator move
   * differs and each sentence names its own.
   *
   * - #4309 `disk_floor_unmet`: the pre-task hygiene pass found the disk floor still unmet
   *   after its own cleanup. Free disk on the box.
   * - #4332 `service_unreachable`: the repo's committed policy declares a service and the
   *   box's own run-start probe found nothing listening on it after the repo's own setup.
   *   Bring the service up on the box (or fix the setup that should have).
   * - #5000 `baseline_stash_stranded`: NOT before the executor — after it. The base probe
   *   could not keep the run's diff in the tree, so the run stopped before committing; same
   *   budget and exclusion, and the sentence says where the change is.
   *
   * Reason sentence:
   * This row's copy of the ENVIRONMENTAL sentence family (read by `failureReasonSentence`, `apps/api/src/routes/tasks.ts`): the BOX refused, not
   * the task — a per-machine fact the next box does not share, so a retry on another box is
   * the remedy rather than anything in the diff. The kinds share one
   * shape ("No box graded the diff — {cause}, so no attempt was charged. That is {whose}, not
   * the task: {remedy}. A task refused this way stops after repeated refusals in a row rather
   * than retrying forever…") and one stop rule: three CONSECUTIVE refusals of the row trip
   * `refusals_exhausted`. #5000's kind is the one whose run DID the work and was graded red: the
   * base probe lost the diff before comparing that red with the base, so its sentence says so,
   * and where the change is.
   *
   * #4332 — the checkout's committed policy declares a service (its `services:` list)
   * and, after the repo's own setup ran, that service accepted no connection on the
   * box's loopback. The survey never ran and no attempt was charged.
   */
  ServiceUnreachable: {
    value: 'service_unreachable',
    ordinal: 35,
    producer: wire(
      'apps/runner/src/modes/worker/team/session/errors.ts#TeamServiceUnreachableError',
    ),
    budget: uncharged(3),
    park: null,
    boxLocal: true,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The repository’s committed policy declares a service and the box’s run-start probe found nothing listening on it after the repository’s own setup, so no agent ran, nothing was written and no attempt was charged. That is the box, not the change: bring the service up on the box (or fix the setup that should have started it). The refusing box is kept off the next attempt; a task refused this way stops after repeated refusals in a row rather than retrying forever.',
      unclaimed:
        'No box graded the diff — a service your repository’s policy declares (its `services:` list) accepted no connection on the box after the repository’s own setup ran, so no agent started and no attempt was charged. That is almost always the box, not the task: the service is down or unreachable there, and the refusing box sits out this task’s next attempt so it lands on another box. A task refused this way stops after repeated refusals in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.',
      ran: 'A service your repository’s policy declares (its `services:` list) accepted no connection on the box after the repository’s own setup ran, so the change was never graded and no attempt was charged — the attempts it does carry were spent by earlier failures of some other kind. That is almost always the box, not the task: the service is down or unreachable there, and the refusing box sits out this task’s next attempt so it lands on another box. A task refused this way stops after repeated refusals in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.',
    },
  },
  /**
   * THE BOX COULD NOT DECODE ITS ASSIGNMENT (plan 101 F4). The push failed the box's
   * schema — measured cause: a mixed-version rollout, a mothership that learned an
   * enum member the box's build does not know — and the box reported it on
   * `fleet.box.<runnerId>.assignment_undecodable` before dropping it, instead of the
   * task sitting `assigned` for the 180-min stuck-task window and then going back to
   * the same box.
   *
   * TWO ROADS SINCE #6237, and this row's budget describes only the second. A box on a
   * release the ledger holds is NOT charged and this kind is not stamped: the task is
   * held for a newer release (`@developerz/db` `queries/tasks/decode-hold.ts`), and the
   * kind lands, attempt untouched, only when the refusing release is at or after the
   * mothership's own (`failDecodeHeldTask`). A box whose build the ledger does not hold
   * keeps the road below.
   *
   * CHARGED and BOX-LOCAL (that second road): the re-pend spends an attempt and keeps
   * the reporting box off the task, and a task only such boxes see ends at
   * `TASK_MAX_ATTEMPTS` instead of looping. `Fail` on exhaustion.
   *
   * Produced at `apps/worker/src/fleet/box-event-sink.ts#rependUndecodable`.
   *
   * Producer:
   * Plan 101 F4: the MOTHERSHIP writes it, from the box's `assignment_undecodable`
   * box event — the box never stamps a run-event code, because it never started a run.
   *
   * Budget:
   * CHARGED and BOX-LOCAL (plan 101 F4): the box that could not decode the push is
   * excluded from the re-pend (`BOX_LOCAL_FAILURE_KINDS` (`../failure-budget.ts`)), and the attempt
   * counter is what ends a push NO box can decode instead of re-pending it forever.
   * `Fail` on exhaustion.
   *
   * Box-local:
   * Plan 101 F4: the build on THAT box could not decode the push; a box on a newer
   * build can. Box-local by construction — the fault is the reporter's binary.
   *
   * Babysit round:
   * The box's build could not decode the assignment (plan 101 F4).
   *
   * Box-at-fault:
   * Plan 101 F4: the box's BUILD is at fault — it cannot decode what the platform
   * sends it, and updating that box is the remedy.
   *
   * Alert sentence:
   * `assignment_undecodable` (plan 101 F4) is the box that could not decode
   * the push — a build or format skew — kept off the task for a window per report.
   *
   * Reason sentence:
   * This row's copy of the ASSIGNMENT sentence family (read by `failureReasonSentence`, `apps/api/src/routes/tasks.ts`): the two ways a
   * task failed before a box ever ran it. An `attempt = 0` row can say no attempt was
   * charged; an `attempt ≥ 1` row cannot say WHICH failure charged its attempts (the
   * counter is shared across kinds, and rows that ended before #6237 were charged by
   * this kind on every box), so its sentence states what the row shows and no more.
   *
   * Failed before any box ran it: undecodable, or never delivered.
   *
   * Plan 101 PR4: the box's build and the platform disagree on the task format,
   * so the box reported the assignment instead of running it (and is kept off it).
   */
  AssignmentUndecodable: {
    value: 'assignment_undecodable',
    ordinal: 41,
    producer: platform('apps/worker/src/fleet/box-event-sink.ts#rependUndecodable'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: true,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The box could not decode the assignment (a build or format skew — almost always a box on an older runner build than the platform sends) and reported it. No attempt is charged: the task is held for a box on a newer runner release, so update the fleet (`box update`). It ends only when the refusing release is at least as new as the platform’s own, or, for a box whose build the release ledger does not hold, after charged retries with that box kept off the task for 30 minutes.',
      unclaimed:
        'A box on a runner build at least as new as the platform’s own could not decode this assignment and reported it instead of running it, so waiting for a newer build has no basis: what the box refused is the task’s stored format, not the box’s age. No attempt was charged, and the task can be retried. The box’s decode error is on the task’s audit trail.',
      ran: 'A box could not decode this assignment — its build and the platform disagree on the task format — so it reported it instead of running it. This task carries charged attempts: its last failed attempt was a decode refusal, and its audit trail shows what each attempt was charged for. A task still pending is waiting for a box that can decode it; a failed one ended either because its attempts ran out or because a box at least as new as the platform’s own refused it, and can be retried once the boxes are updated (`box update`).',
    },
  },
  /**
   * THE MOTHERSHIP COULD NOT DELIVER THE ASSIGNMENT (plan 101 02-2). The outbox relay
   * failed to publish the `fleet.task.<runnerId>` push on every attempt its retry
   * ladder allows (`RELAY_MAX_ATTEMPTS`) and dead-lettered the row, so the task sat
   * `assigned` to a box that never received it until the stuck-task reaper found it.
   * The dead-letter re-pends it at once.
   *
   * CHARGED, NOT BOX-LOCAL: the box never saw the bytes, so there is no box to exclude,
   * and the attempt is what ends a push that can never be sent (a payload past the
   * link's size limit) instead of re-claiming it forever. `Fail` on exhaustion.
   *
   * Produced at `apps/worker/src/dispatch/relay.ts#relayDeadLetter`.
   *
   * Producer:
   * Plan 101 02-2: the MOTHERSHIP writes it when the outbox relay dead-letters the
   * push — no box ever received the assignment, so none stamps a code.
   *
   * Budget:
   * CHARGED, NOT box-local (plan 101 02-2): the relay dead-lettered the push, so no box
   * ever held the bytes and none is excluded. The ladder ends a push that can never be
   * sent. `Fail` on exhaustion.
   *
   * Babysit round:
   * The mothership never delivered the assignment (plan 101 02-2).
   *
   * Box-at-fault:
   * Plan 101 K4: the ACCOUNT has no model for the review lane; any box refuses the same,
   * so paging a machine sends an operator to healthy hardware (the account-side signals
   * carry the remedy). Plan 101 02-2: the PLATFORM's relay could not deliver the push —
   * the box never saw it, and its fleet link and the payload are the platform's to fix.
   *
   * Reason sentence:
   * This row's copy of the ASSIGNMENT sentence family (read by `failureReasonSentence`, `apps/api/src/routes/tasks.ts`): the two ways a
   * task failed before a box ever ran it. These two share one rule: `failOrRetryTask`
   * charges one attempt, re-queues the task and fails it once attempts run out, so
   * only an `attempt ≥ 1` row may say an attempt was charged.
   *
   * Failed before any box ran it: undecodable, or never delivered.
   *
   * Plan 101 wave 3: the outbox dead-lettered the assignment after its retries,
   * so the box never received it; the task is re-pended at once.
   */
  AssignmentUndeliverable: {
    value: 'assignment_undeliverable',
    ordinal: 42,
    producer: platform('apps/worker/src/dispatch/relay.ts#relayDeadLetter'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The platform could not deliver the assignment to the box: the outbox relay failed to publish it on every retry and dead-lettered it (the `outbox.dead_lettered` audit row names the error). An attempt was charged each time; nothing about the box or the task is wrong. Check the fleet link and the payload size, then retry the task.',
      unclaimed:
        'The platform could not deliver this assignment to the box after repeated tries, so the box never received it. The task is re-queued at once.',
      ran: 'The platform could not deliver this assignment to the box after repeated tries, so the box never received it. The task is re-queued at once. An attempt was charged; if it can never be delivered, it fails once the attempts run out.',
    },
  },
  /**
   * THE REPO'S OWN `bin/setup` IS DOWN EVERYWHERE, not on this box: a setup refusal
   * (a {@link GateUnrunnable} from `TeamRepoSetupError` / `RunTreeSetupError`) landing
   * on a repo whose setup has refused with one signature on `REPO_ENV_FAULT_MIN_BOXES`
   * distinct boxes, from `REPO_ENV_FAULT_MIN_SOURCES` distinct tasks or PRs, inside
   * `REPO_ENV_FAULT_WINDOW_MINUTES` (`./repo-env-fault`).
   *
   * The box sends `gate_unrunnable`; the MOTHERSHIP regrades it, because only the
   * mothership sees the other boxes. Prod 2026-09-24, tesote/tesote.ai: two fleet-wide
   * setup outages in one day spent 3/3 attempts on every task they touched, since the
   * box-local re-pend sends each attempt to the next box, which fails identically.
   *
   * NOT CHARGED, with no consecutive cap: nothing about the task was tried. What bounds
   * the loop is the HOLD — the re-pend parks on `TaskHoldReason.RepoEnvFault`, the claim
   * skips it, and the sweep lets ONE task through per probe interval. NOT box-local:
   * excluding the box would teach the queue something false about it.
   *
   * Produced at `apps/worker/src/fleet/repo-env-fault.ts#repoEnvFailureKind`.
   *
   * Producer:
   * The MOTHERSHIP regrades a box's `gate_unrunnable` setup refusal when the repo's
   * setup is down on several boxes at once — only the mothership sees the other boxes,
   * so no box can stamp this code.
   *
   * Budget:
   * NOT CHARGED and UNCAPPED: the repo's setup is down on every box, so the task was
   * never tried. What bounds the loop is the park (this row's `park`, read by `taskFailureRependHold`) and
   * the sweep that lets ONE held task through per `REPO_ENV_PROBE_INTERVAL_MS` — never a
   * counter, because a counter here would end work for an outage it had no part in,
   * which is the 2026-09-24 incident this kind exists to stop repeating.
   *
   * Park:
   * The repo's setup is down on every box: park until the sweep releases the row
   * (a probe passed setup) — the window is the fail-safe if the sweep stops.
   *
   * Babysit round:
   * The repo's own setup is down on every box — nothing about the PR was tried.
   *
   * Box-at-fault:
   * The repo's own setup is down on EVERY box — the one verdict here that is a fact
   * about no box at all. Paging an operator to inspect a healthy machine is the miss.
   *
   * Alert sentence:
   * #4914 — the opposite diagnosis of `DiskFloorUnmet` and `ServiceUnreachable`: EVERY box, not one.
   *
   * Reason sentence:
   * This row's copy of the REPO-ENVIRONMENT sentence family (read by `failureReasonSentence`, `apps/api/src/routes/tasks.ts`, #4914): the repo's
   * own `bin/setup` refused the same way on several boxes at once, so the platform regraded
   * the refusal `repo_env_down`, charged nothing and parked the row. The opposite diagnosis of
   * the environmental kinds: those are a single box, this one is every box.
   */
  RepoEnvDown: {
    value: 'repo_env_down',
    ordinal: 43,
    producer: platform('apps/worker/src/fleet/repo-env-fault.ts#repoEnvFailureKind'),
    budget: uncharged(),
    park: {
      reason: TaskHoldReason.RepoEnvFault,
      backstopMs: REPO_ENV_DOWN_HOLD_MS,
      honorsStatedReset: false,
    },
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The repository’s own `bin/setup` failed the same way on several boxes within half an hour, so this is the repository’s environment (its setup, a registry it pulls from, a database every box shares), not a box and not the change. No agent ran and no attempt was charged; the task is parked, not failed. Fix the cause and push to the default branch — the first setup that passes releases every parked task on its own.',
      unclaimed:
        'No box graded the diff — your repository’s own `bin/setup` failed the same way on several boxes within half an hour, so the fault is the repository’s environment (its setup, a registry it pulls from, a database every box shares), not a box and not the change. No agent ran and no attempt was charged. The task is parked, not failed: `task stalls` names the failing line, a blocker carries the command’s tail, and the first setup that passes — a probe after a push to the default branch, or the periodic one — releases every parked task on its own.',
      ran: 'Your repository’s own `bin/setup` failed the same way on several boxes within half an hour, so the fault is the repository’s environment (its setup, a registry it pulls from, a database every box shares), not a box and not the change. No agent ran and no attempt was charged — the attempts this row carries were spent by earlier failures of some other kind. The task is parked, not failed: `task stalls` names the failing line, a blocker carries the command’s tail, and the first setup that passes — a probe after a push to the default branch, or the periodic one — releases every parked task on its own.',
    },
  },
  /**
   * THE RUN'S OWN DIFF IS STRANDED IN THE BASELINE PROBE'S STASH (#5000). When the diff's gate
   * comes back red, the team session stashes the diff, re-runs the gate on the base, and pops
   * (`apps/runner/src/modes/worker/team/session/base-probe.ts`). When the pop is refused —
   * `refs/stash` is no longer the probe's entry, because the repo's gate stashed on top of it
   * — or git fails it (the gate wrote a path the stash holds), or the PUSH itself saved an
   * entry and then failed to clean the tree, the change is not (all) in the tree. A run that
   * went on from there opened a pull request WITHOUT its change on a red no worse than the
   * base, or saved the stripped tree over the task's saved commit. So the run stops here, before
   * anything is staged, and its error names the checkout, the entry and how to restore it.
   *
   * WHY NOT AN EXISTING MEMBER: {@link CheckoutUnavailable} is a clone that never happened;
   * {@link RunnerError} is the uncoded residual and is CHARGED; {@link BoxBaselineRed} is a
   * verdict about the base, and here the comparison with the base never finished.
   *
   * NOT CHARGED, 3 consecutive, box-local. The diff's red was never compared with the base —
   * the probe lost the diff first — so charging would bill the task for a red that may be the
   * base's. A gate that stashes or collides every time strands every red attempt, which the
   * consecutive bound ends (#3094). Box-local because this box's checkout is left mid-restore
   * (the entry in its stash list, possibly a half-applied pop or push): the box is kept off
   * the task for `BOX_FAULT_EXCLUSION_MINUTES`, and after that — on a one-box fleet, that is
   * the same box — it can take the task again and opens on whatever the probe left there.
   *
   * Producer:
   * #5000 — the team session's base probe could not restore its own stash; one throw site
   * stamps the code, the catch-all forwards it, the classifier enumerates it.
   *
   * Budget:
   * NOT CHARGED, 3 consecutive (#5000): the diff's red was never compared with the base — the
   * probe lost the diff first — so no attempt is spent on a red that may be the base's; a gate
   * that strands every red attempt ends the task on the third in a row, not looping uncharged.
   *
   * Box-local:
   * #5000: THIS box's checkout is left mid-restore — the probe's stash entry holding the
   * run's diff, possibly a half-applied pop or push — so the box sits out the task for the
   * window, and only for the window: after it the same box may reopen on that tree, and on a
   * one-box fleet it is the only box that can. Box-local by construction, not by measurement.
   *
   * Babysit round:
   * The team session's base probe lost the run's diff to its stash (#5000) — not a babysit producer.
   *
   * Box-at-fault:
   * #5000: the machine is healthy — the repo's gate stashed on top of, or wrote into, the
   * base probe's stash. The box holds the stranded change, and the run's error names the
   * entry; the task alert carries that recovery, a box page would not.
   *
   * Alert sentence:
   * THE BOX'S ENVIRONMENT REFUSED THE WORK BEFORE ANY EXECUTOR RAN — no attempt charged,
   * three in a row on the row ends it, the refusing box is kept off the next attempt. Both
   * read like `BoxBaselineRed` because both are environmental refusals; the operator move
   * differs and each sentence names its own.
   *
   * - #4309 `disk_floor_unmet`: the pre-task hygiene pass found the disk floor still unmet
   *   after its own cleanup. Free disk on the box.
   * - #4332 `service_unreachable`: the repo's committed policy declares a service and the
   *   box's own run-start probe found nothing listening on it after the repo's own setup.
   *   Bring the service up on the box (or fix the setup that should have).
   * - #5000 `baseline_stash_stranded`: NOT before the executor — after it. The base probe
   *   could not keep the run's diff in the tree, so the run stopped before committing; same
   *   budget and exclusion, and the sentence says where the change is.
   *
   * #5000 — the run DID the work; the base probe then lost it to its own stash.
   *
   * Reason sentence:
   * This row's copy of the ENVIRONMENTAL sentence family (read by `failureReasonSentence`, `apps/api/src/routes/tasks.ts`): the BOX refused, not
   * the task — a per-machine fact the next box does not share, so a retry on another box is
   * the remedy rather than anything in the diff. The kinds share one
   * shape ("No box graded the diff — {cause}, so no attempt was charged. That is {whose}, not
   * the task: {remedy}. A task refused this way stops after repeated refusals in a row rather
   * than retrying forever…") and one stop rule: three CONSECUTIVE refusals of the row trip
   * `refusals_exhausted`. #5000's kind is the one whose run DID the work and was graded red: the
   * base probe lost the diff before comparing that red with the base, so its sentence says so,
   * and where the change is.
   *
   * #5000 — the diff's gate was red; the check comparing that red with the base could not put
   * the change back. NOT "no box graded the diff": one did. The box sits out the window.
   */
  BaselineStashStranded: {
    value: 'baseline_stash_stranded',
    ordinal: 44,
    producer: wire(
      'apps/runner/src/modes/worker/team/session/errors.ts#TeamBaselineStashStrandedError',
    ),
    budget: uncharged(3),
    park: null,
    boxLocal: true,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The diff’s gate came back red, and the check that compares that red with the base could not put the change back: its stash was stashed over or collided with a path the verify command wrote, or git failed to finish the stash. The run stopped before committing anything and no attempt was charged. The change is not lost: the run’s error names the box’s checkout, the stash entry and how to restore it. That box is kept off the task for 30 minutes — after that, or on a one-box fleet, it can take the task again and starts from what this run left there. A task stopped this way stops for good after repeated stops in a row.',
      unclaimed:
        'The diff’s gate came back red, and the check that compares that red with your repository’s base could not put the change back — its stash was stashed over or collided with a path the verify command wrote, or git failed to finish the stash — so the run stopped before committing anything and no attempt was charged. The change is not lost: the run’s error names the box’s checkout, the stash entry and how to restore it. That box is kept off this task for 30 minutes; after that — or on a single-box fleet — it can pick the task up again and starts from what this run left in that checkout. A task stopped this way stops after repeated stops in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.',
      ran: 'The diff’s gate came back red, and the check that compares that red with your repository’s base could not put the change back — its stash was stashed over or collided with a path the verify command wrote, or git failed to finish the stash — so the run stopped before committing anything and no attempt was charged for it; the attempts this row carries were spent by earlier failures of some other kind. The change is not lost: the run’s error names the box’s checkout, the stash entry and how to restore it. That box is kept off this task for 30 minutes; after that — or on a single-box fleet — it can pick the task up again and starts from what this run left in that checkout. A task stopped this way stops after repeated stops in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.',
    },
  },
  /**
   * The box holding a STARTED run stopped executing without handing it back — its
   * presence key lapsed (the reaper), it booted again still holding the claim (a
   * crash, an OOM-kill), or the claims backstop found it `offline` with work. A box
   * that is gone writes nothing, so no box `error` event exists for this failure: the
   * run's stream simply stops, and the platform writes the kind.
   *
   * Split out of {@link RunnerError}, which these releases used to stamp and CHARGE
   * (plan 101 L8). The bound that charge bought was right — a task that kills every box
   * it lands on must end — and the charge was the wrong way to buy it: it spent the
   * task's ONE ladder on the machine's death, so a single lost box on a task's last
   * attempt ended the task. Prod, tesote/tesote.ai `tsk_4b4aadc3cbca8600d03236d795ffceb7`
   * (2026-09-27): attempts 1–2 spent by `gate_unrunnable` + `verify_failed`, 118 paths of
   * saved WIP, then `worker-2-developerz-ai-vm-3` went dark mid-run at 17:54Z (no drain,
   * no update hop, still offline an hour later) and the reaper's charge ended it
   * `runner_error / attempts_exhausted` at 17:58Z, with an empty reason.
   *
   * Budget:
   * NOT CHARGED, 3 consecutive — the `box_baseline_red` shape. An isolated loss
   * re-pends with the attempt, the saved WIP and the ladder untouched; three losses IN A
   * ROW (the streak, which any other failure kind resets) end the task
   * `refusals_exhausted`, which is the crash-loop bound L8 wanted. A PR already open
   * still ends it on the first loss (`pr_already_open`), as on every arm.
   *
   * Babysit round:
   * The box died under the run — the work did not fail.
   *
   * Box-at-fault:
   * Yes: the machine stopped. `box_offline` pages for the box itself; this names it as
   * the cause on the task.
   */
  BoxLost: {
    value: 'box_lost',
    ordinal: 47,
    producer: platform('packages/db/src/queries/runners/recovery.ts#releaseBoxClaimsTx'),
    budget: uncharged(3),
    park: null,
    boxLocal: false,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The box running this task stopped responding mid-run — it stopped heartbeating or restarted without handing the run back — so the platform took the task back. No attempt was charged: the task goes to another box and resumes from its saved work. Three lost boxes in a row stop it for good.',
      unclaimed:
        'The box running this task stopped responding mid-run — it stopped heartbeating or restarted without handing the run back — so no box error was ever reported, and no attempt was charged for it. A pending task goes to another box and resumes from its saved work; the task stops only after its box is lost three times in a row, or when its pull request is already open. A failed one can be retried.',
      ran: 'The box running this task stopped responding mid-run — it stopped heartbeating or restarted without handing the run back — so no box error was ever reported, and no attempt was charged for it; the attempts this row carries were spent by earlier failures of some other kind. A pending task goes to another box and resumes from its saved work; the task stops only after its box is lost three times in a row, or when its pull request is already open. A failed one can be retried.',
    },
  },
  /**
   * The screen_worker_outdated / box-draining release loop (#5448). The box cannot
   * hand the run back and the task returns to pending at the same attempt, so a
   * fleet-wide skew between box and worker claims → screens → releases the task
   * in milliseconds with nothing to show for it. `releaseTaskOnDrain` bounds the
   * loop at N consecutive `task.released` audit rows for the same
   * `(task, reason)` inside an hour and stamps this kind when the bound trips.
   *
   * Budget:
   * NOT CHARGED, 5 consecutive — `BoxLost`'s shape with the trip condition's
   * ceiling. The platform stamps the kind, not the box.
   *
   * Babysit round:
   * The fleet shape was wrong — the work did not fail, the rollout was.
   *
   * Box-at-fault:
   * Yes when the cause is an old worker (#5444); no when the cause is a drain.
   * Conservative on purpose: `taskFailureBlamesBox` reads true here so a
   * `box_unhealthy` fires on a worker-skew task too — the operator's job is to
   * read the reason, and `task.released` is the row to look at.
   */
  BoxReleaseLoop: {
    value: 'box_release_loop',
    ordinal: 52,
    producer: platform('packages/db/src/queries/runners/recovery.ts#releaseTaskOnDrain'),
    budget: uncharged(5),
    park: null,
    boxLocal: false,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The task was released back to the queue five times in a row for the same reason (the worker could not decode the screen request, or the box kept draining before the run began) — the platform stopped the loop and ended the task. No attempt was charged for any of the releases.',
      unclaimed:
        'No box kept this task past one claim — the same release reason fired five times in a row and the platform ended the loop before a run ever started.',
      ran: 'The task was released back to the queue five times in a row for the same reason (the worker could not decode the screen request, or the box kept draining before the run began) and the platform ended the loop. No attempt was charged for any of the releases; the attempts this row carries were spent by earlier failures of some other kind.',
    },
  },
  /**
   * THE BOX LACKED THE MEMORY THIS GATE NEEDED (#6347) — the repo's own check was killed
   * at the run's memory bound (`outOfMemory` → `team/session/verify.ts` `verifyVerdict`),
   * or the box sat over its memory refuse line and the runner declined the spawn
   * (`box_memory_pressure`). Both arms map here from `NO_VERDICT_FAILURE_CODE`
   * (`team/session/errors.ts`); the memory half was `run_killed` before, and the charged
   * ladder WAS the defect: a gate that wants more memory than the cage mints dies the
   * same way on every same-sized box, so each attempt re-ran the identical oversized
   * gate until the ladder spent (the pr_babysit `run_killed` spike, 0→5/day, 228 kills
   * 2026-10-07 → 10-13). The `disk_floor_unmet` budget, for its reason: the room was the
   * box's to provide, no fix was judged, and a redelivery costs the task nothing.
   *
   * Box-local: the re-pend keeps this box off the task (#3094). CAP-AWARE RE-HOMING (#7081,
   * `../gate-memory-cap.ts`): when the box says the RUN's own per-task cap fired (trip
   * `task_cap` — structured `gate_memory`, or an old box's message text), the failure write
   * records that cap on the task (`tasks.memory_unmet_cap_bytes`, the largest one that
   * failed) and the claim offers the task only to a box whose advertised per-task cap is
   * strictly larger; while no online box serving the account is, the row is held
   * `box_fault_excluded` and `task stalls` names the cap it needs, the largest online, and
   * that a bigger box is the fix. That is the promise `copy.alert` makes. A `unit_limit` or
   * `box_ram` trip, or the `box_memory_pressure` refuse line, records no cap — those are the
   * box's load as much as the gate's size — so for them the pacer is still the exclusion
   * plus the load clearing. Three consecutive trip `refusals_exhausted`, which a human is
   * told about through this row's `copy.alert`.
   */
  GateMemoryUnmet: {
    value: 'gate_memory_unmet',
    ordinal: 59,
    producer: wire('apps/runner/src/modes/worker/team/session/errors.ts#TeamVerifyNoVerdictError'),
    budget: uncharged(3),
    park: null,
    boxLocal: true,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The box lacked the memory the repository's own check needed (killed at the run's memory bound, or over the box's memory refuse line before it could start), so the change was never graded and no attempt was charged. That is the box, not the task: the task goes to another box — one with a larger memory cap clears it — and this box's load may clear on its own. A task refused this way stops after repeated refusals in a row rather than retrying forever.",
      unclaimed:
        "No box graded the diff — the box lacked the memory the repository's own check needed (killed at the run's memory bound, or over the box's memory refuse line before it could start), so no attempt was charged. That is the box, not the task: the task goes to another box, and a pending task retries on its own. A task refused this way stops after repeated refusals in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.",
      ran: "The box lacked the memory the repository's own check needed (killed at the run's memory bound, or over the box's memory refuse line before it could start), so the change was never graded and no attempt was charged — the attempts it does carry were spent by earlier failures of some other kind. That is the box, not the task: the task goes to another box, and this box's load may clear on its own. A task refused this way stops after repeated refusals in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.",
    },
  },
  /**
   * THE #6970 QUARANTINE WAS LICENSED AND THE TREE STILL REFUSED — the team open's
   * recovery parked the foreign work, and either the retry met the SAME parkable refusal
   * (`dirty_worktree` / `untracked_collision`, the tree re-dirtied under the held lock) or
   * the park itself failed (`stash_ineffective`: a stash a live writer undid, or a stash
   * fault). Those are the ONLY graded arms — exactly the producer
   * `TeamCheckoutQuarantineError`'s two (`apps/runner/src/modes/worker/team/session/checkout.ts`);
   * an emit that fails AFTER a successful park, and every no-recovery path, keep the
   * original uncoded refusal and stay `runner_error`.
   *
   * Prod, run_449e972c… (tesote/tesote.ai, 2026-10-23): `team_checkout_quarantined {outcome:
   * parked}`, then milliseconds later the retry refused the same "1 tracked modification(s)".
   * The recovery holds the checkout lock from the first open through the retry (#6970), so
   * the tree the retry judged is the tree THIS box refused twice — the box, not the task.
   * Unstamped, the original codeless `GitSequenceError` (`git/errors.ts` stamps `.code` only
   * on auth failures) graded `runner_error` and CHARGED an attempt for the box's own tree,
   * on a redelivery nothing kept off that box.
   *
   * WHY NOT AN EXISTING MEMBER: {@link CheckoutUnavailable} is a clone that never happened;
   * {@link DiskFloorUnmet} and {@link ServiceUnreachable} refuse in the pre-task passes,
   * before the open, while this is the open's own recovery turning on its tree; and
   * {@link RunnerError} is the charged residual the stamp replaces.
   *
   * Budget:
   * `Uncharged` with `maxConsecutive: 3` — the {@link DiskFloorUnmet} shape, for its reason:
   * nothing was graded (the retry refused before any editor ran, or the park failed before
   * the retry was spent), so the attempt ladder, which bounds *"the fix didn't work"*, has
   * no fix to count. What makes the re-queue worth spending is that the recovery CLEANED
   * the tree on its way through — a park that held leaves the foreign work on
   * `dz/quarantine/<runId>-<ts>` and the next open meets a clean tree — so the redelivery
   * is not doomed to the same refusal. Three consecutive refusals on one box trip
   * `refusals_exhausted`, the backstop that stops a permanently wedged tree re-pending the
   * same row forever at zero attempts spent.
   *
   * Producer:
   * #6970's follow-through — `TeamCheckoutQuarantineError`, the SUBCLASS the recovery
   * throws on its two graded arms, carrying the original refusal verbatim (message byte
   * for byte, `refusal`, the quarantine reason and pathspecs) as `cause` while stamping
   * this kind as `code`. One throw site, one stamp; the wire `code` map
   * ({@link TASK_FAILURE_WIRE_CODE}) grades it by enumeration like every other `wire`
   * kind, and an older mothership grades the unknown code `runner_error` — documented
   * skew, charged as before the stamp, never rejected.
   *
   * Box-local:
   * The box is the subject twice over: the tree that refused twice is this box's own, and
   * the recovery that failed ran on it. `taskFailureBoxLocal` keeps it off the re-pend for
   * `BOX_FAULT_EXCLUSION_MINUTES` (#3094), so the task moves to another box; the clock
   * lapses, so a task whose only capable box refused it is delayed, never stranded. The
   * kind never parks (`park: null`): the re-pend is open to any other box at once.
   *
   * Babysit round:
   * The box's own checkout refused the recovery twice (#6970) — not a babysit producer.
   *
   * Box-at-fault:
   * Yes — the box's tree is what refused, on both arms. Nothing about the diff was read:
   * the open is before the survey, the editors and the gate, so the change was never
   * graded and the operator who goes to the box is the only one who can clear the tree.
   *
   * Alert sentence:
   * This row's copy of the ENVIRONMENTAL sentence family (read by `failureReasonSentence`, `apps/api/src/routes/tasks.ts`): the BOX refused, not
   * the task — the refusing box is kept off the next attempt (the kind is box-local), and
   * three refusals in a row end the row rather than loop it uncharged.
   */
  CheckoutQuarantineFailed: {
    value: 'checkout_quarantine_failed',
    ordinal: 63,
    producer: wire(
      'apps/runner/src/modes/worker/team/session/checkout.ts#TeamCheckoutQuarantineError',
    ),
    budget: uncharged(3),
    park: null,
    boxLocal: true,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The box's checkout refused this task's branch a second time after its own quarantine pass had parked the foreign work — the parked tree still would not take the cut, or the park itself failed. Nothing about the change was judged and no attempt was charged. That is the box, not the task: the task goes to another box, and the refusing box sits this one out for a while. A task refused this way stops after repeated refusals in a row rather than retrying forever.",
      unclaimed:
        "No box graded the diff — the last box to try could not cut the task's branch even after its own quarantine pass parked the foreign work standing in the way, or that park itself failed, so no attempt was charged. That is the box, not the task: the refusing box sits out this task's next attempt so it lands on another box. A task refused this way stops after repeated refusals in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.",
      ran: "The box's checkout refused this task's branch a second time after its own quarantine pass had parked the foreign work — the parked tree still would not take the cut, or the park itself failed — so the change was never graded and no attempt was charged; the attempts this row carries were spent by earlier failures of some other kind. That is the box, not the task: the task goes to another box, and the refusing box sits this one out for a while. A task refused this way stops after repeated refusals in a row rather than retrying forever: a pending task retries on its own, a failed one can be retried.",
    },
  },
  /**
   * THE SCOUT'S READ TOOLS WERE NOT MOUNTED — the `read_tools` arm of the
   * planner-grounding precondition (`scoutGroundingFault`,
   * `apps/runner/src/modes/scout/executor.ts`): the codegraph client could not be
   * mounted for this checkout, so `repo_ask` cannot answer, the plan call's
   * promised repo grounding cannot exist, and the run is REFUSED before the brain
   * is called. No plan is authored and no blocker is minted — a blocker would file
   * a pipeline fault as work for a human.
   *
   * WHY THIS ARM — AND WHAT THE CENSUS DID NOT SAY. The three arms that carried
   * the unnamed `'grounding_missing'` code were `issue_body`, `repo_evidence` and
   * `read_tools`. Which one dominates is decided by the seven-day task-outcomes
   * cluster table — `failure_kind` × `left(payload->>'message', 80)` over failed
   * tasks, counted as distinct TASKS (`taskFailureClustersSqlText`,
   * `packages/db/src/queries/admin/task-outcomes.ts:90`, over the builder
   * `packages/db/src/queries/admin/task-outcomes-sql.ts#taskFailureClustersSql`),
   * read with
   *
   *   bun run scripts/task-failure-report.ts --days 7 --out <a dir OUTSIDE the repo>
   *
   * ITS OUTCOME — measured, or refused with the exact refusal — is recorded on the
   * dated progress entry (`docs/progress.md`, 2026-10-24), NOT here: this row
   * quotes no distribution and no number stands in for one. The window that
   * statement reads is the 7×24 h preceding the moment it runs (#3872), not a
   * UTC day.
   *
   * THE EVIDENCE THIS ROW STANDS ON, and its limits: the only dated per-arm prod
   * population the repository records for the three unnamed arms — 26 `read_tools`
   * refusals on prod, 2026-09-03, from a box whose BOOT install of the codegraph
   * binary hit a network fault and refused every scout run for the life of the
   * process (`apps/runner/src/modes/scout/executor.ts`, the `ensureCodegraphBinary`
   * seam's own docblock) — against NO recorded count at all for `repo_evidence`
   * (the SKIPPED checkout) or for `issue_body`. That is a RANKING, not a
   * distribution.
   *
   * AND WHAT THE `no_token` NUMBER IS NOT. The 4 of 196 `scout_started` events
   * carrying `refreshReason: 'no_token'` (7 days to 2026-08-28,
   * `apps/runner/src/tree-contract.ts:20`) are the slice-1 REFRESH skipping on a
   * PRESENT checkout: the fetch had no credential, so the tree went STALE and was
   * never proven (`assessDefaultBranchTree` refuses every status but `current` and
   * `refreshed`) — the population the `tree_unproven` arm grades, and that arm
   * carries its OWN wire code. The `repo_evidence` arm is the SKIPPED checkout
   * (`repoRoot === null`), a state that produces no refresh verdict at all; this
   * repository records NO count for it.
   *
   * WHAT WOULD FALSIFY IT, and what it costs to correct: a census whose largest
   * `runner_error` cluster carries another arm's message. The correction is one
   * line of `SCOUT_GROUNDING_WIRE_CODE` plus this row's name — the BUDGET below is
   * the residual's own, so the spend is the same whichever arm is named.
   *
   * Producer:
   * The `read_tools` arm of `scoutGroundingFault` — ONE producer (the
   * `ScoutGroundingMissingError` constructor throws verbatim from
   * `scout/executor.ts`'s precondition check), and the wire `code` map
   * `SCOUT_GROUNDING_WIRE_CODE` there names this kind under the
   * `'scout_read_tools_absent'` code. The audit chain carries no separate event
   * for this arm: the throw site is the only producer, and the `error` event the
   * loop forwards already names the cause via the wire `code`.
   *
   * Budget:
   * `runner_error`'s EXACT budget — `Charged`, the full `TASK_MAX_ATTEMPTS`
   * ladder, exhausted `Fail`. These rows are
   * `runner_error` today, so a relabel that ALSO re-budgeted would move retry
   * behaviour under cover of a naming change; this one moves the LABEL and
   * nothing else. The census that would have justified a tighter ladder (a tool
   * that is never there again wants ONE attempt, like `ToolUnavailable`) is the
   * one whose outcome the dated progress entry records; until that entry says
   * otherwise, the ladder stays where the residual had it.
   *
   * Box-local:
   * NOT box-local, deliberately: the fault is this box's own codegraph mount, and
   * the #3094 exclusion (keeping the refusing box off the re-pend) is a decision
   * about WHERE the next attempt runs, not about what this failure costs. It
   * belongs to its own change with its own evidence — the same rule the
   * 2026-08-29 re-partition states for `verify_failed` (`./tree.ts`).
   *
   * Babysit round:
   * CHARGED, exactly as {@link RunnerError} carried it and as these rows carried
   * it while they WERE the residual: a round that ends this way spends its
   * babysit ladder. The box could not mount its read tools, so the refusal is
   * the box's own — and the spend stays the residual's, unchanged, because a
   * naming change must not move what a round costs.
   *
   * Box-at-fault:
   * Yes — the box could not mount the client the scout grounds on. Nothing about
   * the change was read: the refusal happens before the brain is called, so no
   * diff was ever proposed and no gate ever judged one. The operator who goes to
   * the box is the only one who can clear it.
   *
   * Alert sentence:
   * Split from the residual so the sentence can name the CAUSE — the box had no
   * read tools mounted, so the promised grounding did not exist — and the remedy
   * the run's own `grounding` event carries beside it.
   *
   * Reason sentence:
   * The same sentence as the alert arm: this refusal happens before any run
   * produced work, so the row reads the same whether or not a box claimed it.
   */
  ScoutReadToolsAbsent: {
    value: 'scout_read_tools_absent',
    ordinal: 64,
    producer: wire('apps/runner/src/modes/scout/executor.ts#ScoutGroundingMissingError'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The scout precondition found no read tools mounted on this box — the codegraph client could not be mounted for this checkout, so the promised repo grounding did not exist and the run never started. Mount codegraph for this repo; the work is re-queued and eligible for another box.',
      unclaimed:
        'No box claimed this — the scout precondition refused the run because no codegraph client could be mounted for this checkout; mount codegraph for this repo.',
      ran: 'The scout precondition found no read tools mounted on this box — the codegraph client could not be mounted for this checkout, so the promised repo grounding did not exist and the run never started. Mount codegraph for this repo.',
    },
  },
  /**
   * THIS MACHINE COULD NOT OPEN THE SEALED KEY IT WAS HANDED (#6765, R4: no fallback
   * plan). The scout's model gate answered `unwrap_failed`: a credential IS sealed on the
   * assignment, but this box's key-encryption key (`DZ_BOX_KEK_BASE64`) is missing or
   * wrong, so it cannot turn it into a model. Before R4 the scout floored into a one-group
   * plan; now the run ends failed with the reason.
   *
   * Producer:
   * `ScoutRunFailedError` with reason `kek_unwrap_failed`, graded by `SCOUT_FAILURE_KIND`.
   *
   * Budget:
   * `task_cage_unmintable`'s: no model ran, so nothing is charged, and three refusals in a
   * row end it.
   *
   * Babysit round:
   * Copied from `task_cage_unmintable` (`Uncharged`).
   *
   * Box-at-fault:
   * YES. The account sealed a credential correctly; this box's KEK cannot open it, and
   * another box can. So it is box-local and blames the box.
   */
  BoxKekUnwrapFailed: {
    value: 'box_kek_unwrap_failed',
    ordinal: 71,
    producer: wire('apps/runner/src/modes/scout/brain/run.ts#ScoutRunFailedError'),
    budget: uncharged(3),
    park: null,
    boxLocal: true,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The box could not open the sealed model key it was handed: its key-encryption key (DZ_BOX_KEK_BASE64) is missing or wrong, so no model ran and no attempt was charged. That is the box, not the task: another box retries it; fix this box's KEK. Three such refusals in a row end it.",
      unclaimed:
        "No box claimed this — the last box could not open the sealed model key it was handed (its key-encryption key is missing or wrong), so no model ran. Another box retries it; fix that box's KEK.",
      ran: "This machine could not open the sealed key it was handed (its key-encryption key is missing or wrong). Another machine retries; fix the machine's KEK.",
    },
  },
  /**
   * THE MOTHERSHIP DID NOT ANSWER THE CLAIM'S `git_token` MINT. The box's first mint for
   * the claim timed out or found no responder on every try of its retry ladder
   * (`GIT_TOKEN_MINT_RETRY_DELAYS_MS`, `apps/runner/src/modes/worker/git-token-retry.ts`),
   * so the run holds no credential for a repo it must reach. The worker answers that verb
   * and rolls `Recreate` on every merge; the ladder outlasts a roll, so what reaches this
   * kind is an outage longer than one.
   *
   * Before it, the missing token degraded to `null` and the run died later on the
   * symptom: a clone skipped `no_token` (`checkout_unavailable`, one charged attempt) or
   * git's `could not read Username` (`git_auth`, one charged attempt). Prod census
   * 2026-11-01, 72 h: 9 tasks logged `git_token_unavailable {reason: transport_error,
   * trigger: claim}`, 6 of them inside worker-roll minutes; 7 ended failed
   * (3 `checkout_unavailable`, 3 `git_auth`, 1 `onboarding_blocked`).
   *
   * Producer:
   * `apps/runner/src/checkout.ts#GitTokenUnreachableError`, thrown by the mint core
   * (`modes/worker/credentials.ts` `resolveCodingGitCredential`) on the claim's mint only.
   *
   * Budget:
   * Uncharged, three in a row end it (`screen_unavailable`'s rule): nothing ran and the
   * task is not at fault, but a responder that never answers must end the task rather
   * than re-pend it forever at zero attempts spent.
   *
   * Babysit round:
   * The round never started: no credential.
   *
   * Box-at-fault:
   * No. The mothership did not answer; any box would have waited the same.
   *
   * Failure rate:
   * NOT excluded, on purpose. An excluded code must be counted somewhere else
   * (`apps/worker/src/fleet-alerts/baseline-red.ts`: a per-repository alarm, or a
   * named not-a-refusal), and this one fits neither: it is a platform outage, not a
   * repository's, and it is not the design working. Excluding it would make a worker
   * that stays unreachable past the ladder invisible; in the rate it moves the one
   * fleet-wide number that pages for exactly that.
   */
  GitTokenUnreachable: {
    value: 'git_token_unreachable',
    ordinal: 74,
    producer: wire('apps/runner/src/checkout.ts#GitTokenUnreachableError'),
    budget: uncharged(3),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The platform did not answer the box’s request for a GitHub credential for this repository, so nothing ran and no attempt was charged. It retries on its own; three in a row end it.',
      unclaimed:
        'The platform did not answer the last box’s request for a GitHub credential, so nothing ran and nothing was charged. It retries on its own.',
      ran: 'The platform did not answer the box’s request for a GitHub credential for this repository, so nothing ran and no attempt was charged. The task retries on its own and stops after three such failures in a row.',
    },
  },
} as const satisfies Record<string, FailureKindRow>;
